import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
const AIAssistant = lazy(() => import('../components/AIAssistant'));
import {
  Camera,
  Video,
  Globe,
  Zap,
  Check,
  Star,
  ArrowRight,
  ChevronRight,
  ChevronLeft,
  ShieldCheck,
  Clock,
  MessageCircle,
  CheckCircle2,
  CreditCard,
  Users,
  MapPin,
  Car,
  X,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { saveBooking, saveLead } from '../lib/firestore';
import { addDoc, doc, collection, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { getDb } from '../lib/firebase';
import { V } from '../constants';
import * as BookingAnalytics from '../lib/analytics';
import { useBookingStore, Selections, FormState } from '../store/useBookingStore';
import { HelpCircle } from 'lucide-react';
import { OWNER_WHATSAPP_NUMBER, WHATSAPP_NUMBER } from '../constants';

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────

interface ServiceItem { key: string; name: string; price: number; tag?: string; desc?: string; deliverables?: string[]; }
interface AddonItem { key: string; name: string; price: number; desc?: string; }

function ServiceSection({
  title, icon, description, items, selected, onSelect,
  addonItems, selectedAddons, onToggleAddon,
  hasSecondCam, onToggleSecondCam, secondCamRecommended,
  hasThirdCam, onToggleThirdCam,
  extraSection,
  upsellNote,
  skipped = false,
  onSkip,
}: {
  title: string; icon: React.ReactNode; description: string;
  items: ServiceItem[]; selected: string; onSelect: (key: string) => void;
  addonItems: AddonItem[]; selectedAddons: string[]; onToggleAddon: (key: string) => void;
  hasSecondCam?: boolean; onToggleSecondCam?: () => void; secondCamRecommended?: boolean;
  hasThirdCam?: boolean; onToggleThirdCam?: () => void;
  extraSection?: React.ReactNode;
  upsellNote?: string;
  skipped?: boolean;
  onSkip?: () => void;
}) {
  const quietDignity = useBookingStore(s => s.quietDignity);
  const sel = useBookingStore(s => s.sel);

  const skipLabelTitle = title === 'Photography' ? 'Photography'
    : title === 'Live Streaming' ? 'Streaming'
    : title === 'Cinematic Video' ? 'Video'
    : 'Drone coverage';
  const skipLabelLower = skipLabelTitle.toLowerCase();

  return (
    <div className="space-y-6 card-default p-8 bg-card-bg border-border">
      {onSkip && (
        <button
          type="button"
          onClick={onSkip}
          aria-pressed={skipped}
          className={cn(
            'w-full flex items-center justify-between p-3 min-h-[48px] rounded-xl border text-xs transition-all',
            skipped
              ? 'bg-white/5 border-white/10 text-ink3'
              : 'bg-white/[0.02] border-white/5 text-ink3 hover:border-white/10'
          )}
        >
          <span>{skipped ? `${skipLabelTitle} skipped — tap to restore` : `We don't need ${skipLabelLower} — skip this service`}</span>
          <div className={cn('w-9 h-5 rounded-full relative transition-all', skipped ? 'bg-gold' : 'bg-white/10')}>
            <div className={cn('absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all', skipped ? 'left-4' : 'left-0.5')} />
          </div>
        </button>
      )}

      <div className="flex items-center gap-4">
        <div className="p-3 bg-gold-bg rounded-2xl text-gold">{icon}</div>
        <div>
          <h2 className="text-2xl font-serif text-ink">{title}</h2>
          <p className="text-sm text-ink2">{description}</p>
        </div>
      </div>

      {title === 'Photography' && (
        <div className="inline-flex items-center gap-2 px-3 py-2 bg-gold/5 border border-gold/15 rounded-full text-[10px] text-gold font-medium">
          <span>🖼️</span> 100–300 images by tier · private gallery · 48-hour delivery
        </div>
      )}
      {title === 'Live Streaming' && (
        <div className="inline-flex items-center gap-2 px-3 py-2 bg-gold/5 border border-gold/15 rounded-full text-[10px] text-gold font-medium">
          <span>🌍</span> 60% of our bookings include streaming for diaspora families
        </div>
      )}
      {title === 'Cinematic Video' && (
        <div className="inline-flex items-center gap-2 px-3 py-2 bg-gold/5 border border-gold/15 rounded-full text-[10px] text-gold font-medium">
          <span>🎬</span> 5–8 min to 30–45 min film by tier · colour graded · diaspora-ready
        </div>
      )}
      {title === 'Drone Aerial Coverage' && (
        <div className="inline-flex items-center gap-2 px-3 py-2 bg-gold/5 border border-gold/15 rounded-full text-[10px] text-gold font-medium">
          <span>🚁</span> Licensed CAA pilot · cinematic aerial film + high-res stills
        </div>
      )}

      <div className={cn(skipped && 'opacity-40 pointer-events-none')}>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {items.map(item => (
          <button
            key={item.key}
            onClick={() => onSelect(item.key)}
            className={cn(
              'p-6 rounded-[32px] border text-left transition-all duration-500 relative overflow-hidden flex flex-col',
              selected === item.key
                ? 'bg-gold-bg border-gold shadow-[0_0_25px_rgba(201,168,76,0.12)]'
                : 'bg-white/[0.02] border-white/5 hover:border-gold/20'
            )}
          >
            {item.name.toLowerCase().includes('signature') && (
              <div className="absolute top-0 right-0 bg-gold text-[8px] font-bold px-3 py-1.5 rounded-bl-2xl uppercase tracking-widest text-[#0A0A0A]">Most Requested</div>
            )}
            <div className="font-bold text-sm text-ink mb-1">{item.name}</div>
            <div className="price-figure text-2xl mb-3 text-gold font-serif">{quietDignity ? 'Contact Us' : `KSh ${item.price.toLocaleString()}`}</div>
            <div className="text-[11px] text-ink2 mb-4 leading-relaxed line-clamp-3">{item.desc}</div>

            <div className="mb-4 text-[9px] text-gold/60 font-bold uppercase tracking-widest flex items-center gap-1.5 line-height-none bg-gold/5 w-fit px-2 py-1 rounded-full">
              <span className="animate-pulse">🔥</span> {Math.floor(Math.random() * 8) + 12} families chose this this week
            </div>

            <div className="mt-auto">
              {item.deliverables && selected === item.key && (
                <div className="space-y-2 pt-4 border-t border-white/5">
                  {item.deliverables.map((d, i) => (
                    <div key={i} className="flex items-start gap-2 text-[10px] text-ink2">
                      <Check size={12} className="text-gold mt-0.5 flex-shrink-0" />
                      {d}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selected === item.key && !item.deliverables && (
              <div className="absolute bottom-4 right-4 text-gold">
                <Check size={16} />
              </div>
            )}
          </button>
        ))}
      </div>

      {selected && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 pt-2">
          {extraSection}

          {upsellNote && (
            <p className="text-[10px] text-gold mt-2 italic font-medium px-4 py-2 bg-gold/5 rounded-lg border border-gold/10 inline-block">✨ {upsellNote}</p>
          )}

          {addonItems.length > 0 && (
            <div className="pt-6 border-t border-white/5">
              <span className="eyebrow !mb-4">Enhance with Add-ons</span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {addonItems.map(addon => (
                  <button
                    key={addon.key}
                    onClick={() => onToggleAddon(addon.key)}
                    className={cn(
                      'flex items-center justify-between p-4 rounded-xl border text-xs transition-all duration-300 text-left gap-3',
                      selectedAddons.includes(addon.key)
                        ? 'bg-gold-bg border-gold/30 text-gold shadow-[0_0_15px_rgba(192,103,59,0.05)]'
                        : 'bg-white/[0.02] border-white/5 text-ink2 hover:border-gold/30'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn('w-4 h-4 rounded border flex items-center justify-center transition-colors', selectedAddons.includes(addon.key) ? 'bg-gold border-gold' : 'border-white/10')}>
                        {selectedAddons.includes(addon.key) && <Check size={10} className="text-bg" />}
                      </div>
                      <span className="font-bold">{addon.name}</span>
                    </div>
                    <span className="text-gold font-serif">{quietDignity ? 'Private Pricing' : `KSh ${addon.price.toLocaleString()}`}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {onToggleSecondCam && (
            <div className="pt-2 space-y-2">
              {secondCamRecommended && (
                <div className="flex items-start gap-2 p-3 bg-gold/8 border border-gold/20 rounded-xl text-[11px] text-ink2 leading-relaxed">
                  <span className="text-gold text-sm">💡</span>
                  <span>
                    <strong className="text-gold">Recommended for your gathering size.</strong>{' '}
                    At 150+ guests, moments happen simultaneously. A second camera ensures nothing is missed.
                  </span>
                </div>
              )}
              <button
                type="button"
                onClick={onToggleSecondCam}
                className={cn(
                  'flex items-center justify-between w-full p-4 rounded-xl border text-xs transition-all duration-300',
                  hasSecondCam
                    ? 'bg-gold-bg border-gold/30 text-gold shadow-[0_0_15px_rgba(192,103,59,0.05)]'
                    : 'bg-white/[0.02] border-white/5 text-ink2 hover:border-gold/30'
                )}
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    'w-4 h-4 rounded border flex items-center justify-center transition-colors',
                    hasSecondCam ? 'bg-gold border-gold' : 'border-white/10'
                  )}>
                    {hasSecondCam && <Check size={10} className="text-bg" />}
                  </div>
                  <span className="font-bold">
                    Add Second Camera Operator
                    {secondCamRecommended && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest bg-gold/15 text-gold px-2 py-0.5 rounded-full font-bold">
                        Recommended
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  {title === 'Photography' && hasSecondCam && (
                    <span className="text-[10px] bg-gold/10 text-gold px-2 py-0.5 rounded-full font-bold uppercase tracking-widest">
                      +50 Images Added
                    </span>
                  )}
                  <span className="text-gold font-serif">
                    {quietDignity ? 'Private' : `KSh ${CAM2.toLocaleString()}`}
                  </span>
                </div>
              </button>
              {onToggleThirdCam && hasSecondCam && (
                <button
                  type="button"
                  onClick={onToggleThirdCam}
                  className={cn(
                    'flex items-center justify-between w-full p-4 rounded-xl border text-xs transition-all duration-300 mt-2',
                    hasThirdCam
                      ? 'bg-gold-bg border-gold/30 text-gold shadow-[0_0_15px_rgba(192,103,59,0.05)]'
                      : 'bg-white/[0.02] border-white/5 text-ink2 hover:border-gold/30'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'w-4 h-4 rounded border flex items-center justify-center transition-colors',
                      hasThirdCam ? 'bg-gold border-gold' : 'border-white/10'
                    )}>
                      {hasThirdCam && <Check size={10} className="text-bg" />}
                    </div>
                    <span className="font-bold">
                      Add Third Camera Operator
                      {sel?.crowd === 'l' && (
                        <span className="ml-2 text-[9px] uppercase tracking-widest bg-gold/15 text-gold px-2 py-0.5 rounded-full font-bold">
                          Recommended for 250+
                        </span>
                      )}
                    </span>
                  </div>
                  <span className="text-gold font-serif">
                    {quietDignity ? 'Private' : `KSh ${CAM2.toLocaleString()}`}
                  </span>
                </button>
              )}
            </div>
          )}
        </motion.div>
      )}
      </div>
    </div>
  );
}

function SidebarSection({ label, basePrice, extras }: { label: string; basePrice: number; extras: { name: string; price: number }[] }) {
  const quietDignity = useBookingStore(s => s.quietDignity);
  if (basePrice === 0 && extras.length === 0) return null;
  return (
    <div className="py-6 border-b border-white/5 last:border-0">
      <span className="eyebrow !mb-4">{label}</span>
      <div className="space-y-2.5">
        {basePrice > 0 && (
          <div className="flex justify-between items-center text-sm">
            <span className="text-ink2/70">Base Package</span>
            <span className="text-gold font-serif">{quietDignity ? 'Private Pricing' : `KSh ${basePrice.toLocaleString()}`}</span>
          </div>
        )}
        {extras.map((ex, i) => (
          <div key={i} className="flex justify-between items-center text-xs">
            <span className="text-ink3 flex-1 pr-4">{ex.name}</span>
            <span className="text-gold/80 italic font-serif">{quietDignity ? 'Private Pricing' : `KSh ${ex.price.toLocaleString()}`}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewItem({ label, value, price }: { label: string; value: string; price: number }) {
  const quietDignity = useBookingStore(s => s.quietDignity);
  return (
    <div className="flex justify-between items-center border-b border-white/5 pb-4 last:border-0 last:pb-0">
      <div>
        <span className="eyebrow !mb-1 !text-[9px]">{label}</span>
        <p className="font-bold text-ink text-sm">{value}</p>
      </div>
      <span className="price-figure text-xl">{quietDignity ? 'Private' : `KSh ${price.toLocaleString()}`}</span>
    </div>
  );
}

function TrustBadge({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-4 text-xs text-ink3">
      <div className="text-gold bg-white/5 p-2 rounded-lg">{icon}</div>
      <span className="leading-tight">{text}</span>
    </div>
  );
}

const Input = React.forwardRef<HTMLInputElement, { label: string; error?: string; required?: boolean; id: string } & React.InputHTMLAttributes<HTMLInputElement>>(
  ({ label, error, required, id, ...props }, ref) => {
    return (
      <div className="space-y-1.5">
        <label htmlFor={id} className="text-xs uppercase tracking-widest font-bold text-ink2/170 block pl-1">
          {label} {required && <span className="text-gold">*</span>}
        </label>
        <input
          id={id}
          ref={ref}
          {...props}
          className={cn(
            'w-full input-default min-h-[48px]',
            error ? 'border-red bg-red-bg/10' : 'border-ink/8'
          )}
        />
        <div className="min-h-[20px]">
          {error && <p className="text-[10px] text-red font-medium pl-1 italic flex items-center gap-1">
            ⚠ {error}
          </p>}
        </div>
      </div>
    );
  }
);
Input.displayName = 'Input';



// ─── PRICING (v4 exact figures) ───────────────────────────────────────────────

const PHOTO_BASE = { ph2: 20000, ph4: 25000, phF: 30000 };
const PHOTO_ADDONS = { phr: 4500, phb: 10000 };
const STREAM_BASE = { st2: 20000, st4: 25000, stF: 30000 };
const STREAM_ADDONS = { stm: 5000, stw: 5000, stc: 2000, st_edited: 5000 };
const VIDEO_BASE = { vi2: 20000, vi4: 30000, viF: 35000 };
const VIDEO_EDIT = { vie1: 0, vie2: 5000, vie3: 12000 };
const VIDEO_ADDONS = { vir: 3000, vit: 2000 };
const DRONE_BASE = { dr2: 25000, dr4: 30000, drF: 35000 };
const DRONE_ADDONS = { drm: 6000, drl: 8000 };
const CAM2 = 15000;

// ─── OVERTIME & DURATIONS ──────────────────────────────────────────────────
const TIER_HOURS: Record<string, number> = {
  ph2: 2, ph4: 4, phF: 8,
  st2: 2, st4: 4, stF: 8,
  vi2: 2, vi4: 4, viF: 8,
  dr2: 2, dr4: 4, drF: 8
};

const OT_RATES: Record<string, number> = {
  photo: 5000,
  stream: 5000,
  video: 7000,
  drone: 6000
};

const addHoursToTime = (time: string, hours: number) => {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const date = new Date();
  date.setHours(h, m, 0);
  date.setHours(date.getHours() + hours);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

// ─── TRANSPORT RATES ─────────────────────────────────────────────────────────
// Based on Toyota Estima 2.8L @ ~10.5 km/L, petrol KSh 178.28 (EPRA Mar-Apr 2026)
// Commercial hire rate = fuel cost × 2.8 (driver time + wear + parking)
// All zones are round-trip from Westlands base

const TRANSPORT_ZONES = [
  { key: 'cbd', label: 'CBD / Westlands / Upperhill', desc: 'Nairobi city centre & immediate surrounds', fee: 1500 },
  { key: 'suburbs', label: 'Suburbs', desc: 'Kilimani · Lavington · Langata · Karen · Ruaka', fee: 2500 },
  { key: 'outskirts', label: 'Outskirts', desc: 'Kiambu · Ruiru · Rongai · Thika · Ngong', fee: 4000 },
  { key: 'outside', label: 'Outside Nairobi', desc: 'Enter distance — fee calculated per km', fee: null },
] as const;

const TRANSPORT_PER_KM = 45; // KSh 45/km round trip (fuel + driver + wear)
const TRANSPORT_OUTSIDE_MIN = 6000; // minimum charge for outside Nairobi
const CREW_MEMBERS = {
  photography: [],
  video: [],
  streaming: [],
  drone: [],
  assist: []
};



// ─── SERVICE ITEM DATA ────────────────────────────────────────────────────────

const STREAM_ITEMS = [
  { key: 'st2', name: 'Intimate Stream', price: 20000, tag: 'Best for small services', desc: 'Up to 2 hrs · 1 camera · 1 private link', deliverables: ['Up to 2 hours', 'YouTube / Facebook Live', 'Cemetery arrival streamed', 'Church/home service streamed', 'Graveside streamed', 'Full recording delivered'] },
  { key: 'st4', name: 'Signature Stream', price: 25000, tag: 'Recommended for diaspora', desc: '2–4 hrs · Multi-platform priority', deliverables: ['2–4 hours coverage', 'YouTube + Facebook simultaneously', 'Cemetery to graveside streamed', 'Lifetime access to recording'] },
  { key: 'stF', name: 'Complete Stream', price: 30000, tag: 'Full Day', desc: '4–8 hrs · Global Broadcast Network', deliverables: ['Full day coverage', 'YouTube + Facebook + Twitch', 'Procession streamed', 'Graveside & burial streamed', 'Lifetime access'] },
];
const STREAM_ADDON_ITEMS = [
  { key: 'stm', name: 'Multi-Platform Streaming', price: 5000, desc: 'Broadcast simultaneously to YouTube, Facebook and Twitch for large diaspora audiences' },
  { key: 'st_edited', name: 'Livestream Editing', price: 5000, desc: 'Professionally edited high-res recording delivered after the event' },
  { key: 'stw', name: 'WhatsApp Live Photo Updates', price: 5000, desc: 'Real-time photo previews sent to your family WhatsApp group throughout the service' },
  { key: 'stc', name: 'Timestamped Recording', price: 2000, desc: 'Recording with named chapter markers so family can jump to any moment' },
];

const PHOTO_ITEMS = [
  { key: 'ph2', name: 'Intimate Memorial', price: 20000, tag: 'Best for small services', desc: 'Church & Graveside · Up to 2 hrs', deliverables: ['100 hand-finished images included', 'Cemetery arrival & procession', 'Church/home service', 'Eulogy & tributes', 'Family portraits', 'Graveside & committal', 'Private Pixieset gallery', 'Delivered within 48 hrs'] },
  { key: 'ph4', name: 'Signature Memorial', price: 25000, tag: 'Most families choose this', desc: 'Church & Graveside · Up to 4 hrs', deliverables: ['200 hand-finished images included', 'Cemetery arrival', 'Full ceremony coverage', 'All family groupings', 'Audio recording of tributes', 'Procession to graveside', 'Graveside & burial', 'Private gallery · 48-hr delivery'] },
  { key: 'phF', name: 'Complete Tribute', price: 30000, tag: 'Full Day', desc: 'Full Day · 4–8 hrs', deliverables: ['300 hand-finished images included', 'Cemetery arrival', 'Church/home service', 'All eulogies & prayers', 'All family portraits', 'Procession to graveside', 'Graveside & burial', 'Private gallery · 48-hr delivery'] },
];

const PHOTO_ADDON_ITEMS = [
  { key: 'phr', name: 'Rush Delivery — 24 Hours', price: 4500, desc: 'Gallery ready within 24 hours instead of standard 48' },
  { key: 'phb', name: 'Premium Photo Book — 20 Pages', price: 10000, desc: 'Lay-flat printed book · curated images · delivered in 2 weeks' },
];

const VIDEO_ITEMS = [
  { key: 'vi2', name: 'Intimate Tribute', price: 20000, tag: 'Best for small services', desc: '1 camera · 2-hr session · colour graded', deliverables: ['1 cinema-grade camera', '2-hour session', 'Cemetery through graveside', 'Colour graded footage', '5–8 min highlight film', 'Delivered within 5 days'] },
  { key: 'vi4', name: 'Signature Memorial', price: 30000, tag: 'Most families choose this', desc: '1 camera · 4-hr session · cemetery to graveside', deliverables: ['1 cinema-grade camera', '4-hour session', 'Cemetery through graveside', 'Colour graded footage', '15–25 min extended output', 'Delivered within 7 days'] },
  { key: 'viF', name: 'Complete Tribute', price: 35000, tag: 'Full Day', desc: '1 camera · Full day · all locations', deliverables: ['1 cinema-grade camera', 'Full day session', 'All family moments', 'Broadcast-quality grade', '30–45 min full output', 'Delivered within 10 days'] },
];
const VIDEO_EDIT_ITEMS = [
  { key: 'vie1', name: '5–8 Min Highlight Film', price: 0, tag: 'Included', desc: 'Cinematic highlight — best moments, set to music, colour graded' },
  { key: 'vie2', name: '15–25 Min Extended Cut', price: 5000, tag: '', desc: 'Extended cut covering eulogies, all ceremony segments, hymns and graveside' },
  { key: 'vie3', name: '30–45 Min Full Archive', price: 12000, tag: 'Complete Archive', desc: 'Documentary-style unabridged service — the permanent family archive' },
];
const VIDEO_ADDON_ITEMS = [
  { key: 'vir', name: 'Raw Footage Archive', price: 3000, desc: 'All unedited camera footage on a private download link — yours forever' },
  { key: 'vit', name: 'Personalised Titles & Tribute Text', price: 2000, desc: 'Custom name cards, dates, scripture verses and tribute text overlaid in the film' },
];

const DRONE_ITEMS = [
  { key: 'dr2', name: '2-Hour Aerial Session', price: 25000, tag: '', desc: 'Cemetery, church/home venue, procession and graveside', deliverables: ['Licensed drone pilot', 'Cemetery & church aerials', 'Procession aerial shots', 'Graveside aerials', '2–5 min cinematic reel', 'Aerial still photos'] },
  { key: 'dr4', name: '4-Hour Extended Aerial', price: 30000, tag: '', desc: 'Multi-pass aerial — all locations covered', deliverables: ['Licensed drone pilot', 'All locations multi-pass', 'Extended cinematic reel', 'Aerial still photos'] },
  { key: 'drF', name: 'Full Day Aerial Coverage', price: 35000, tag: 'Full Day', desc: 'All-day aerial — integrated into full production', deliverables: ['Licensed drone pilot', 'Full day on location', 'Extended cinematic aerial reel', 'Integrated into full edit'] },
];
const DRONE_ADDON_ITEMS = [
  { key: 'drm', name: 'Aerial + Ground Integration Edit', price: 6000, desc: 'Drone and ground footage cut together into one unified cinematic production' },
  { key: 'drl', name: 'Live Aerial Stream Overlay', price: 8000, desc: 'Drone view shown as picture-in-picture during the live stream in real time' },
];

// ─── CROWD DATA ───────────────────────────────────────────────────────────────

const CROWD_OPTIONS = [
  { id: 's', range: 'Up to 150', label: 'Intimate Service', cameras: '1 camera · 1 operator', rationale: '1 camera captures everything at this size — every seat has a clear sightline.' },
  { id: 'm', range: '150 – 250', label: 'Standard Service', cameras: '2 cameras · 2 operators', rationale: 'At this size, moments start happening simultaneously. A second camera ensures nothing is missed.' },
  { id: 'l', range: '250+', label: 'Large Community', cameras: '3 cameras · 3 operators', rationale: 'Large services have multiple focal points. Three cameras give you a complete picture no single operator could capture.' },
];
const CROWD_LABEL: Record<string, string> = {
  s: 'Up to 150 guests (1 camera)',
  m: '150–250 guests (Two-Camera Upgrade)',
  l: '250+ guests (Full Crew — 3 cameras)',
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────

const fmt = (n: number) => `KSh ${n.toLocaleString()}`;
const tomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; };

function calcTotal(sel: Selections): number {
  let t = 0;
  if (sel.photoOpt) { t += PHOTO_BASE[sel.photoOpt as keyof typeof PHOTO_BASE] || 0; }
  sel.photoAddons.forEach(k => { t += PHOTO_ADDONS[k as keyof typeof PHOTO_ADDONS] || 0; });
  if (sel.streamOpt) { t += STREAM_BASE[sel.streamOpt as keyof typeof STREAM_BASE] || 0; }
  sel.streamAddons.forEach(k => { t += STREAM_ADDONS[k as keyof typeof STREAM_ADDONS] || 0; });
  if (sel.videoOpt) { t += VIDEO_BASE[sel.videoOpt as keyof typeof VIDEO_BASE] || 0; }
  if (sel.videoEdit) { t += VIDEO_EDIT[sel.videoEdit as keyof typeof VIDEO_EDIT] || 0; }
  sel.videoAddons.forEach(k => { t += VIDEO_ADDONS[k as keyof typeof VIDEO_ADDONS] || 0; });
  if (sel.droneOpt) { t += DRONE_BASE[sel.droneOpt as keyof typeof DRONE_BASE] || 0; }
  sel.droneAddons.forEach(k => { t += DRONE_ADDONS[k as keyof typeof DRONE_ADDONS] || 0; });
  // Second cameras
  if (sel.photoSecondCam && sel.photoOpt) t += CAM2;
  if (sel.streamSecondCam && sel.streamOpt) t += CAM2;
  if (sel.videoSecondCam && sel.videoOpt) t += CAM2;
  // Third cameras are user-controlled (thirdCam state) — added in component body

  return t;
}

function calcTransport(sel: Selections): number {
  if (!sel.transportZone) return 0;
  if (sel.transportZone === 'outside') {
    const fee = sel.transportKm * TRANSPORT_PER_KM;
    return Math.max(fee, TRANSPORT_OUTSIDE_MIN);
  }
  const zone = TRANSPORT_ZONES.find(z => z.key === sel.transportZone);
  return zone?.fee || 0;
}

function calcOvertime(sel: Selections, form: FormState) {
  if (!form.startTime || !form.endTime) return { total: 0, hours: 0 };

  const [h1, m1] = form.startTime.split(':').map(Number);
  const [h2, m2] = form.endTime.split(':').map(Number);

  const start = new Date(); start.setHours(h1, m1, 0, 0);
  let end = new Date(); end.setHours(h2, m2, 0, 0);

  // If end time is before start time, assume it's the next day
  if (end < start) end.setDate(end.getDate() + 1);

  const actualDurationMs = end.getTime() - start.getTime();
  let totalOT = 0;
  let maxExcess = 0;

  // Photography
  if (sel.photoOpt) {
    const includedMs = TIER_HOURS[sel.photoOpt] * 3600000;
    if (actualDurationMs > includedMs) {
      const excessH = Math.ceil((actualDurationMs - includedMs) / 3600000);
      totalOT += excessH * OT_RATES.photo;
      maxExcess = Math.max(maxExcess, excessH);
    }
  }
  // Stream
  if (sel.streamOpt) {
    const includedMs = TIER_HOURS[sel.streamOpt] * 3600000;
    if (actualDurationMs > includedMs) {
      const excessH = Math.ceil((actualDurationMs - includedMs) / 3600000);
      totalOT += excessH * OT_RATES.stream;
      maxExcess = Math.max(maxExcess, excessH);
    }
  }
  // Video
  if (sel.videoOpt) {
    const includedMs = TIER_HOURS[sel.videoOpt] * 3600000;
    if (actualDurationMs > includedMs) {
      const excessH = Math.ceil((actualDurationMs - includedMs) / 3600000);
      totalOT += excessH * OT_RATES.video;
      maxExcess = Math.max(maxExcess, excessH);
    }
  }
  // Drone
  if (sel.droneOpt) {
    const includedMs = TIER_HOURS[sel.droneOpt] * 3600000;
    if (actualDurationMs > includedMs) {
      const excessH = Math.ceil((actualDurationMs - includedMs) / 3600000);
      totalOT += excessH * OT_RATES.drone;
      maxExcess = Math.max(maxExcess, excessH);
    }
  }

  return { total: totalOT, hours: maxExcess };
}


async function sendEmailConfirmation(bookingId: string, form: FormState, sel: Selections, total: number) {
  try {
    // Build email content
    const emailContent = `
      Booking Confirmation - Faraja Media
      
      Dear ${form.firstName} ${form.lastName},
      
      Thank you for choosing Faraja Media for your memorial service needs. Your booking has been received and is being processed.
      
      Booking ID: ${bookingId}
      
      Client Information:
      - Name: ${form.firstName} ${form.lastName}
      - WhatsApp: ${form.whatsapp}
      - Email: ${form.email}
      - Relationship to deceased: ${form.relation}
      - Name of deceased: ${form.deceased}
      - Service date: ${form.serviceDate}
      - Service time: ${form.startTime || 'To be confirmed'}
      - Venue: ${form.venue || 'To be confirmed'}
      - Expected crowd: ${CROWD_LABEL[sel.crowd]}
      
      Services Selected:
      ${sel.photoOpt ? `- Photography: ${PHOTO_BASE[sel.photoOpt as keyof typeof PHOTO_BASE]} KES` : ''}
      ${sel.streamOpt ? `- Live Streaming: ${STREAM_BASE[sel.streamOpt as keyof typeof STREAM_BASE]} KES` : ''}
      ${sel.videoOpt ? `- Video Production: ${VIDEO_BASE[sel.videoOpt as keyof typeof VIDEO_BASE]} KES` : ''}
      ${sel.droneOpt ? `- Drone Coverage: ${DRONE_BASE[sel.droneOpt as keyof typeof DRONE_BASE]} KES` : ''}
      
      Total Cost: KES ${total.toLocaleString()}
      
      Next Steps:
      1. Our team will contact you within 2 hours to confirm availability
      2. You'll receive a detailed invoice via email and WhatsApp
      3. 75% deposit is required to secure your booking
      4. Payment details: M-Pesa Paybill 247247 | Account 502401
      
      For immediate assistance, please contact us:
      - WhatsApp: +254 721 296 564
      - Email: info@farajamedia.com
      
      We look forward to serving you with dignity and professionalism during this important time.
      
      Best regards,
      Faraja Media Team
      Nairobi, Kenya
      www.farajamedia.com
    `;

    // In a real implementation, you would use an email service like SendGrid, Nodemailer, or Firebase Functions

    console.log('Email confirmation content:', emailContent);
    console.log('Email would be sent to:', form.email);

    // Simulate email send success
    return true;
  } catch (error) {
    console.error('Error sending email confirmation:', error);
    return false;
  }
}

function buildWAMessage(
  sel: Selections,
  form: FormState,
  total: number,
  refSource = '',
  thirdCam = { photo: false, stream: false, video: false }
): string {
  // ── Name lookups ──
  const photoNames: Record<string, string> = { ph2: 'Intimate Memorial', ph4: 'Signature Memorial', phF: 'Complete Tribute' };
  const streamNames: Record<string, string> = { st2: 'Intimate Stream', st4: 'Signature Stream', stF: 'Complete Stream' };
  const videoNames: Record<string, string> = { vi2: 'Intimate Tribute', vi4: 'Signature Memorial', viF: 'Complete Tribute' };
  const droneNames: Record<string, string> = { dr2: 'Drone 2-Hour Aerial', dr4: 'Drone 4-Hour Extended', drF: 'Drone Full Day Aerial' };
  const editNames: Record<string, string> = {
    vie1: 'Edit: 5-8 Min Highlight Film (included)',
    vie2: `Edit: 15 - 25 Min Extended Cut - ${fmt(5000)} `,
    vie3: `Edit: 30 - 45 Min Full Archive - ${fmt(12000)} `,
  };
  const addonNames: Record<string, string> = {
    phr: `Rush Delivery 24hrs - ${fmt(4500)} `,
    phb: `Premium Photo Book - ${fmt(8000)} `,
    stm: `Multi - Platform Streaming - ${fmt(5000)} `,
    st_edited: `Livestream Editing - ${fmt(5000)} `,
    stw: `WhatsApp Live Photos - ${fmt(5000)} `,
    stc: `Timestamped Recording - ${fmt(2000)} `,
    vir: `Raw Footage Archive - ${fmt(3000)} `,
    vit: `Personalised Titles & Text - ${fmt(2000)} `,
    drm: `Aerial + Ground Integration - ${fmt(6000)} `,
    drl: `Live Aerial Stream Overlay - ${fmt(8000)} `,
  };

  // Helper — treat "na", "n/a", blank as unspecified
  const clean = (v: string) => (v && v.trim().toLowerCase() !== 'na' && v.trim().toLowerCase() !== 'n/a') ? v.trim() : null;

  // ── Build services block ──
  const services: string[] = [];

  if (sel.photoOpt) {
    const base = PHOTO_BASE[sel.photoOpt as keyof typeof PHOTO_BASE];
    const baseImages = sel.photoOpt === 'ph2' ? 100 : sel.photoOpt === 'ph4' ? 200 : 300;
    const finalImages = sel.photoSecondCam ? baseImages + 50 : baseImages;

    services.push(`Photography - ${photoNames[sel.photoOpt]} `);
    services.push(`  Base price:          ${fmt(base)} `);
    services.push(`  Deliverables:        ${finalImages} hand-finished images`);
    if (sel.photoSecondCam) services.push(`  + 2nd Camera Bonus:   +50 images included`);
    sel.photoAddons.forEach(k => services.push(`  + ${addonNames[k]} `));
    if (sel.photoSecondCam) services.push(`  + Second Camera:      ${fmt(CAM2)} `);
    if (thirdCam.photo && sel.photoSecondCam) services.push(`  + Third Camera Operator: ${fmt(CAM2)} `);
  }

  if (sel.streamOpt) {
    const base = STREAM_BASE[sel.streamOpt as keyof typeof STREAM_BASE];
    if (services.length) services.push('');
    services.push(`Live Streaming - ${streamNames[sel.streamOpt]} `);
    services.push(`  Base price:          ${fmt(base)} `);
    sel.streamAddons.forEach(k => services.push(`  + ${addonNames[k]} `));
    if (sel.streamSecondCam) services.push(`  + Second Camera:      ${fmt(CAM2)} `);
    if (thirdCam.stream && sel.streamSecondCam) services.push(`  + Third Camera Operator: ${fmt(CAM2)} `);
  }

  if (sel.videoOpt) {
    const base = VIDEO_BASE[sel.videoOpt as keyof typeof VIDEO_BASE];
    if (services.length) services.push('');
    services.push(`Cinematic Video - ${videoNames[sel.videoOpt]} `);
    services.push(`  Base price:          ${fmt(base)} `);
    if (sel.videoEdit) services.push(`  ${editNames[sel.videoEdit]} `);
    sel.videoAddons.forEach(k => services.push(`  + ${addonNames[k]} `));
    if (sel.videoSecondCam) services.push(`  + Second Camera:      ${fmt(CAM2)} `);
    if (thirdCam.video && sel.videoSecondCam) services.push(`  + Third Camera Operator: ${fmt(CAM2)} `);
  }

  if (sel.droneOpt) {
    const base = DRONE_BASE[sel.droneOpt as keyof typeof DRONE_BASE];
    if (services.length) services.push('');
    services.push(`Drone Aerial - ${droneNames[sel.droneOpt]} `);
    services.push(`  Base price:          ${fmt(base)} `);
    sel.droneAddons.forEach(k => services.push(`  + ${addonNames[k]} `));
  }

  // ── Transport block ──
  const transportFee = calcTransport(sel);
  const transportZoneLabel = TRANSPORT_ZONES.find(z => z.key === sel.transportZone)?.label || '';
  const transportLine = sel.transportZone
    ? `${transportZoneLabel}${sel.transportZone === 'outside' ? ` (${sel.transportKm} km)` : ''} - ${fmt(transportFee)} `
    : 'To be quoted - please confirm venue';

  // Crew block (Removed per User Request)


  // ── Date / time ──
  const dateStr = form.serviceDate
    ? new Date(form.serviceDate + 'T00:00:00').toLocaleDateString('en-KE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : 'Not specified';
  const timeStr = form.startTime
    ? `${form.startTime}${form.endTime ? ' - ' + form.endTime : ''} `
    : 'To be confirmed';

  // ── Deposit & totals ──
  const grandTotal = sel.transportZone ? total + transportFee : total;
  const deposit75 = Math.round(grandTotal * 0.75);
  const balance25 = grandTotal - deposit75;

  // ── Assemble ──
  const lines: string[] = [
    '----------------------------',
    '*FARAJA MEDIA - BOOKING REQUEST*',
    '----------------------------',
    '',
    '*CLIENT*',
    `Name:         ${form.firstName} ${form.lastName} `,
    `WhatsApp:     ${form.whatsapp} `,
    ...(clean(form.email) ? [`Email:        ${form.email} `] : []),
    `Relationship: ${form.relation} `,
    ...(sel.diaspora ? [
      `Location:     ${clean(form.diasporaCountry) || 'Outside Kenya'} (Diaspora)`,
      ...(clean(form.kenyaContact) ? [`Kenya Contact: ${form.kenyaContact} `] : []),
    ] : []),
    '',
    '*SERVICE DATE & VENUE*',
    `Deceased:     ${clean(form.deceased) || 'To be confirmed'} `,
    `Date:         ${dateStr} `,
    `Time:         ${timeStr} `,
    `Venue:        ${clean(form.venue) || 'To be confirmed'} `,
    ...(clean(form.venue2) ? [`Second Venue: ${form.venue2} `] : []),
    `Crowd:        ${CROWD_LABEL[sel.crowd]} `,
    ...(clean(form.keyMoments) ? ['', `Key Moments:  ${form.keyMoments} `] : []),
    ...(clean(form.notes) ? [`Instructions: ${form.notes} `] : []),
    '',
    '----------------------------',
    '*SERVICES & QUOTATION*',
    '----------------------------',
    '',
    ...(services.length ? services : ['No services selected - please contact us']),
    '',
    '- - - - - - - - - - - - - - -',
    `Services Total:   ${fmt(total - calcOvertime(sel, form).total)} `,
    ...(calcOvertime(sel, form).total > 0 ? [`Overtime (${calcOvertime(sel, form).hours}h):   + ${fmt(calcOvertime(sel, form).total)} `] : []),
    `Transport:        ${transportLine} `,
    ...(sel.transportZone ? [`Grand Total:      ${fmt(grandTotal)} `] : []),
    '',
    `75% Deposit:      ${fmt(Math.round(grandTotal * 0.75))}`,
    `Balance on day:   ${fmt(grandTotal - Math.round(grandTotal * 0.75))}`,
    'M-Pesa Paybill 247247 · Account 502401',
    '',
    '----------------------------',
    sel.transportZone
      ? 'Please confirm availability to proceed.'
      : 'Please confirm availability. Transport quote to follow.',
  ];


  return lines.join('\n');
}



// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

const DRAFT_KEY = 'farajaBookingDraft';
const LEAD_KEY = 'farajaLeadId';

const generateLeadId = () => 
  `lead_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const getOrCreateLeadId = (): string => {
  try {
    const existing = localStorage.getItem(LEAD_KEY);
    if (existing) return existing;
    const fresh = generateLeadId();
    localStorage.setItem(LEAD_KEY, fresh);
    return fresh;
  } catch {
    return generateLeadId();
  }
};

const loadDraft = (): Partial<FormState> => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed : {};
  } catch {
    return {};
  }
};

export default function FarajaBooking() {
  const { step, setStep, sel, updateSel, form, updateForm, quietDignity, toggleQuietDignity } = useBookingStore();

  useEffect(() => {
    const draft = loadDraft();
    if (Object.keys(draft).length > 0) {
      updateForm(draft);
    }
  }, []);

  useEffect(() => {
    try {
      const { firstName, lastName, whatsapp, email, serviceDate, startTime, endTime } = form;
      localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({ firstName, lastName, whatsapp, email, serviceDate, startTime, endTime })
      );
    } catch {
      // Ignore storage errors
    }
  }, [form.firstName, form.lastName, form.whatsapp, form.email, form.serviceDate, form.startTime, form.endTime]);

  const [secondCamOptOut, setSecondCamOptOut] = useState<{
    photo: boolean; stream: boolean; video: boolean;
  }>({ photo: false, stream: false, video: false });

  const [thirdCam, setThirdCam] = useState<{
    photo: boolean; stream: boolean; video: boolean;
  }>({ photo: false, stream: false, video: false });

  // Auto-tick second cameras for 150+ crowds, respecting client opt-outs
  useEffect(() => {
    if (sel.crowd === 'm' || sel.crowd === 'l') {
      const updates: Partial<Selections> = {};
      if (sel.photoOpt && !secondCamOptOut.photo && !sel.photoSecondCam) updates.photoSecondCam = true;
      if (sel.streamOpt && !secondCamOptOut.stream && !sel.streamSecondCam) updates.streamSecondCam = true;
      if (sel.videoOpt && !secondCamOptOut.video && !sel.videoSecondCam) updates.videoSecondCam = true;
      if (Object.keys(updates).length > 0) updateSel(updates);
    } else if (sel.crowd === 's') {
      // Small crowd: untick everything, clear opt-outs
      const updates: Partial<Selections> = {};
      if (sel.photoSecondCam) updates.photoSecondCam = false;
      if (sel.streamSecondCam) updates.streamSecondCam = false;
      if (sel.videoSecondCam) updates.videoSecondCam = false;
      if (Object.keys(updates).length > 0) updateSel(updates);
      if (secondCamOptOut.photo || secondCamOptOut.stream || secondCamOptOut.video) {
        setSecondCamOptOut({ photo: false, stream: false, video: false });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel.crowd, sel.photoOpt, sel.streamOpt, sel.videoOpt, secondCamOptOut]);

  const leadSavedRef = useRef(false);

  useEffect(() => {
    const anyService = !!(sel.photoOpt || sel.streamOpt || sel.videoOpt || sel.droneOpt);
    if (!anyService || leadSavedRef.current) return;
    const timer = setTimeout(async () => {
      try {
        const db = getDb();
        const leadId = getOrCreateLeadId();
        const total = calcTotal(sel) + calcOvertime(sel, form).total;
        await setDoc(doc(db, 'leads', leadId), {
          leadId,
          status: 'calculator_only',
          services: {
            photoOpt: sel.photoOpt || '',
            streamOpt: sel.streamOpt || '',
            videoOpt: sel.videoOpt || '',
            droneOpt: sel.droneOpt || '',
          },
          crowd: sel.crowd || '',
          transportZone: sel.transportZone || '',
          estimatedTotal: total || 0,
          deviceType: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
          source: 'calculator',
          refSource: refSource || '',
          referrer: typeof document !== 'undefined' ? document.referrer : '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }, { merge: true });
        leadSavedRef.current = true;
      } catch (err) {
        console.error('[Lead capture: calculator]', err);
      }
    }, 2500); // debounce 2.5s so we don't fire on every slider tick
    return () => clearTimeout(timer);
  }, [sel.photoOpt, sel.streamOpt, sel.videoOpt, sel.droneOpt, sel.crowd, sel.transportZone, sel.photoAddons, sel.streamAddons, sel.videoAddons, sel.droneAddons]);

  const contactSavedRef = useRef(false);

  useEffect(() => {
    const digits = (form.whatsapp || '').replace(/\D/g, '');
    const hasPhone = digits.length >= 7;
    const hasEmail = /\S+@\S+\.\S+/.test(form.email || '');
    if ((!hasPhone && !hasEmail) || contactSavedRef.current) return;
    const timer = setTimeout(async () => {
      try {
        const db = getDb();
        const leadId = getOrCreateLeadId();
        await updateDoc(doc(db, 'leads', leadId), {
          firstName: form.firstName || '',
          lastName: form.lastName || '',
          fullName: `${form.firstName || ''} ${form.lastName || ''}`.trim(),
          email: form.email || '',
          whatsapp: form.whatsapp || '',
          phone: form.whatsapp || '',
          status: 'contact_captured',
          contactCapturedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        contactSavedRef.current = true;
      } catch (err) {
        // If lead doesn't exist yet, create it
        try {
          const db = getDb();
          const leadId = getOrCreateLeadId();
          await setDoc(doc(db, 'leads', leadId), {
            leadId,
            firstName: form.firstName || '',
            lastName: form.lastName || '',
            fullName: `${form.firstName || ''} ${form.lastName || ''}`.trim(),
            email: form.email || '',
            whatsapp: form.whatsapp || '',
            phone: form.whatsapp || '',
            status: 'contact_captured',
            source: 'calculator',
            deviceType: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          }, { merge: true });
          contactSavedRef.current = true;
        } catch (err2) {
          console.error('[Lead capture: contact]', err2);
        }
      }
    }, 1500); // 1.5s debounce after last keystroke
    return () => clearTimeout(timer);
  }, [form.whatsapp, form.email, form.firstName, form.lastName]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
const navigate = useNavigate();
  const [waSent, setWaSent] = useState(false);
  const [refSource, setRefSource] = useState('');
  const [dateStatus, setDateStatus] = useState<'none' | 'checking' | 'available'>('none');
  const [showLegacyModal, setShowLegacyModal] = useState(false);
  const [svcSkipped, setSvcSkipped] = useState<{
    photo: boolean; stream: boolean; video: boolean; drone: boolean;
  }>({ photo: false, stream: false, video: false, drone: false });
  

  const whatsappRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);



  useEffect(() => {
    // Capture UTM / referral source from URL on mount
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const src = params.get('ref') || params.get('utm_source') || '';
      if (src) setRefSource(src);
    }
  }, []);

  // Simulate scarcity date availability check
  useEffect(() => {
    if (form.serviceDate) {
      setDateStatus('checking');
      const timer = setTimeout(() => {
        setDateStatus('available');
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      setDateStatus('none');
    }
  }, [form.serviceDate]);

  useEffect(() => {
    BookingAnalytics.trackBookingStep(step, step === 1 ? 'Build Coverage' : step === 2 ? 'Client Details' : 'Final Quotation');
  }, [step]);


  // Auto-calculate end time based on max duration of selected tiers
  useEffect(() => {
    if (form.startTime) {
      const activeTiers = [sel.photoOpt, sel.streamOpt, sel.videoOpt, sel.droneOpt].filter(Boolean);
      if (activeTiers.length > 0) {
        const maxHours = Math.max(...activeTiers.map(t => TIER_HOURS[t] || 0));
        const newEndTime = addHoursToTime(form.startTime, maxHours);
        updateForm({ endTime: newEndTime });
      }
    }
  }, [form.startTime, sel.photoOpt, sel.streamOpt, sel.videoOpt, sel.droneOpt]);


  const ot = calcOvertime(sel, form);
  const thirdCamTotal =
    (thirdCam.photo && sel.photoOpt && sel.photoSecondCam ? CAM2 : 0) +
    (thirdCam.stream && sel.streamOpt && sel.streamSecondCam ? CAM2 : 0) +
    (thirdCam.video && sel.videoOpt && sel.videoSecondCam ? CAM2 : 0);
  const total = calcTotal(sel) + ot.total + thirdCamTotal;


  // ── Helpers ──
  const toggleList = (arr: string[], key: string) => {
    const i = arr.indexOf(key);
    if (i >= 0) { const a = [...arr]; a.splice(i, 1); return a; }
    return [...arr, key];
  };

  const toggleSkip = (svc: 'photo' | 'stream' | 'video' | 'drone') => {
    const skipped = !svcSkipped[svc];
    setSvcSkipped(s => ({ ...s, [svc]: skipped }));
    if (skipped) {
      if (svc === 'photo')  { updateSel({ photoOpt: '', photoImg: '', photoAddons: [], photoSecondCam: false }); setThirdCam(prev => ({ ...prev, photo: false })); }
      if (svc === 'stream') { updateSel({ streamOpt: '', streamAddons: [], streamSecondCam: false }); setThirdCam(prev => ({ ...prev, stream: false })); }
      if (svc === 'video')  { updateSel({ videoOpt: '', videoEdit: '', videoAddons: [], videoSecondCam: false }); setThirdCam(prev => ({ ...prev, video: false })); }
      if (svc === 'drone')  updateSel({ droneOpt: '', droneAddons: [] });
    }
  };

  const toggleCam2Manual = (svc: 'photo' | 'stream' | 'video') => {
    const key = svc === 'photo' ? 'photoSecondCam' : svc === 'stream' ? 'streamSecondCam' : 'videoSecondCam';
    const currentVal = sel[key] as boolean;
    // Record opt-out when client unticks a camera that was auto-added
    setSecondCamOptOut(prev => ({ ...prev, [svc]: currentVal }));
    updateSel({ [key]: !currentVal });
    // If unticking the second camera, also clear the third camera
    if (currentVal === true) {
      setThirdCam(prev => ({ ...prev, [svc]: false }));
    }
  };

  const toggleCam3Manual = (svc: 'photo' | 'stream' | 'video') => {
    setThirdCam(prev => ({ ...prev, [svc]: !prev[svc] }));
  };

  // ── Validation ──
  // includeConsent = false when navigating Step 2 → 3 (consent lives on Step 3)
  const validate = (includeConsent = true) => {
    const currentForm = useBookingStore.getState().form;
    // Guarantee fresh data from refs for critical mobile inputs
    const freshWhatsApp = whatsappRef.current?.value || currentForm.whatsapp;
    const freshEmail = emailRef.current?.value || currentForm.email;

    const e: Record<string, string> = {};
    if (!currentForm.firstName.trim()) e.firstName = 'Required';
    if (!currentForm.lastName.trim()) e.lastName = 'Required';

    if (!freshWhatsApp.trim()) e.whatsapp = 'WhatsApp number is required';
    else if (freshWhatsApp.replace(/\D/g, '').length < 7) e.whatsapp = 'Enter a valid number';

    if (!freshEmail.trim()) e.email = 'Email is required';
    else if (freshEmail.trim().length > 3 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(freshEmail)) e.email = 'Enter a valid email';

    if (!currentForm.relation) e.relation = 'Required';
    if (!currentForm.deceased.trim()) e.deceased = 'Required';
    if (!currentForm.serviceDate) e.serviceDate = 'Service date is required';
    if (!currentForm.startTime) e.startTime = 'Required';
    if (!currentForm.endTime) e.endTime = 'End time is required';

    if (!currentForm.venue.trim()) e.venue = 'Required';
    const anyService = !!(sel.photoOpt || sel.streamOpt || sel.videoOpt || sel.droneOpt);
    if (!anyService) e.service = 'Please select at least one service on Step 1';
    if (includeConsent && !currentForm.consent) e.consent = 'Please confirm the consent statement';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (waSent) return;
    if (!validate()) { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setIsSubmitting(true);

    // Build and open WhatsApp synchronously — must happen before any await
    // so mobile browsers don't block the popup.
    try {
      const whatsappMessageEarly = buildWAMessage(sel, form, total, refSource, thirdCam);
      const maxLengthEarly = 2000;
      const truncatedMessageEarly = whatsappMessageEarly.length > maxLengthEarly
        ? whatsappMessageEarly.substring(0, maxLengthEarly) + '...[Message truncated]'
        : whatsappMessageEarly;
      const encodedMessageEarly = encodeURIComponent(truncatedMessageEarly);
      const whatsappUrlEarly = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessageEarly}`;
      const ownerUrlEarly = `https://wa.me/${OWNER_WHATSAPP_NUMBER}?text=NEW_BOOKING_ALERT_%0A%0A${encodedMessageEarly}`;

      window.open(ownerUrlEarly, '_blank', 'noopener,noreferrer');
      window.open(whatsappUrlEarly, '_blank', 'noopener,noreferrer');
      setWaSent(true);
    } catch (earlyErr) {
      console.error('[Early WhatsApp open failed, continuing to save]', earlyErr);
    }
    try {
      // First generate the service pricing breakdown exclusively for the backend email
      const invoiceLines = buildWAMessage(sel, form, total, refSource, thirdCam)
        .split('*SERVICES & QUOTATION*')[1]
        .split('----------------------------')[0]
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0 && !line.includes('----') && !line.includes('─ ─') && !line.includes('Services Total:'));

      const bookingData = {
        leadId: getOrCreateLeadId(),
        firstName: form.firstName,
        lastName: form.lastName,
        whatsapp: form.whatsapp,
        email: form.email,
        relation: form.relation,
        deceased: form.deceased,
        serviceDate: form.serviceDate,
        startTime: form.startTime,
        venue: form.venue,
        crowd: CROWD_LABEL[sel.crowd],
        services: {
          ...(sel.photoOpt && { photo: sel.photoOpt }),
          ...(sel.streamOpt && { stream: sel.streamOpt }),
          ...(sel.videoOpt && { video: sel.videoOpt }),
          ...(sel.droneOpt && { drone: sel.droneOpt }),
        },
        priceBreakdown: invoiceLines,
        addons: [
          ...sel.photoAddons, ...sel.streamAddons,
          ...sel.videoAddons, ...sel.droneAddons,
          ...(sel.photoSecondCam ? ['Photo Second Camera'] : []),
          ...(sel.streamSecondCam ? ['Stream Second Camera'] : []),
          ...(sel.videoSecondCam ? ['Video Second Camera'] : []),
        ],
        total,
        diaspora: sel.diaspora,
        // Optional fields — only included when non-empty
        ...(form.venue2 && { venue2: form.venue2 }),
        ...(form.endTime && { endTime: form.endTime }),
        ...(form.notes && { notes: form.notes }),
        ...(form.keyMoments && { keyMoments: form.keyMoments }),
        ...((form.refOther || refSource) && { referral: form.refOther || refSource }),
        ...(sel.diaspora && form.diasporaCountry && { diasporaCountry: form.diasporaCountry }),
        ...(sel.diaspora && form.kenyaContact && { kenyaContact: form.kenyaContact }),
        ...(sel.transportZone && {
          transport: {
            zone: sel.transportZone,
            ...(sel.transportZone === 'outside' && { km: sel.transportKm }),
            fee: calcTransport(sel),
          }
        }),
      };

      let bookingId = '';
      let leadId = '';
      let leadSaved = false;

      // Zero Lead Loss Logic: Try to save booking first
      try {
        let savedId: string | null = null;
        try {
          const timeoutPromise = new Promise<null>((resolve) =>
            setTimeout(() => resolve(null), 4000)
          );
          savedId = await Promise.race([
            saveBooking(bookingData),
            timeoutPromise,
          ]);
        } catch (err) {
          console.error('[Booking save timed out or failed]', err);
        }
        bookingId = savedId || `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        console.log('Booking saved with ID:', bookingId);
        leadSaved = true;

        try {
          const db = getDb();
          await updateDoc(doc(db, 'leads', getOrCreateLeadId()), {
            status: 'booked',
            bookingId: bookingId,
            convertedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        } catch (err) {
          console.error('[Lead update on booking]', err);
        }

        if (typeof window !== 'undefined' && (window as any).trackLeadSaved) (window as any).trackLeadSaved(total);
      } catch (bookingError) {
        console.log('Booking save failed, saving to leads collection instead');

        // Fallback: Save to leads collection to ensure zero lead loss
        try {
          const leadData = {
            ...bookingData,
            bookingAttempt: true,
            originalError: bookingError instanceof Error ? bookingError.message : String(bookingError),
            fallbackToLeads: true,
            ref_source: refSource || 'direct'
          };

          // Import saveLead function
          const leadId = await saveLead(leadData);
          console.log('Lead saved successfully with ID:', leadId);
          bookingId = leadId;
          leadSaved = true;
        } catch (leadError) {
          console.error('Failed to save both booking and lead:', leadError);
          throw leadError;
        }
      }

      if (typeof window !== 'undefined' && (window as any).trackWhatsAppClick) (window as any).trackWhatsAppClick();

      // Analytics tracking
      BookingAnalytics.trackBookingComplete(bookingId || leadId, total);
      BookingAnalytics.trackWhatsAppClick(refSource || 'direct', 'FarajaBooking');

      // Send email confirmation (always send when we have an ID)
      if (bookingId || leadId) {
        sendEmailConfirmation(bookingId || leadId, form, sel, total).catch((err) => {
          console.error('Email confirmation failed (non-blocking):', err);
        });
      }

      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      try { localStorage.removeItem(LEAD_KEY); } catch {}

      setIsSuccess(true);
      navigate('/booking-confirmed', {
        state: {
          bookingId: bookingId || leadId,
          name: `${form.firstName || ''} ${form.lastName || ''}`.trim(),
          email: form.email || '',
          whatsapp: form.whatsapp || '',
          total: total || 0,
          serviceDate: form.serviceDate || '',
        },
        replace: true,
      });
    } catch (error) {
      console.error('Booking error:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : 'No stack trace',
        type: typeof error
      });

      // Show actual error to user for debugging
      const actualError = error instanceof Error ? error.message : String(error);
      alert(`Booking Error: ${actualError}\n\nPlease try again or contact us directly via WhatsApp for immediate assistance.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // SUCCESS SCREEN
  // ─────────────────────────────────────────────────────────────────────────────
  const faqSchemaData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How much does funeral livestreaming and photography cost in Kenya?",
        "acceptedAnswer": { "@type": "Answer", "text": "Professional funeral media costs vary based on the extent of coverage. Generally, our photography starts at KSh 20,000, while multi-camera livestreaming ranges from KSh 20,000 for intimate setups to over KSh 30,000 for comprehensive, large-scale event production. Use our free calculator above to get an exact quote." }
      },
      {
        "@type": "Question",
        "name": "Can we share this cost estimate with our funeral planning committee?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes, absolutely. Once you calculate your services, submit the request to instantly receive a formal, broken-down quotation via WhatsApp or email. This makes it incredibly easy for your Harambee committee or family treasurer to budget accurately without missing any hidden fees." }
      },
      {
        "@type": "Question",
        "name": "What exactly is included in the funeral media package?",
        "acceptedAnswer": { "@type": "Answer", "text": "Our transparent pricing covers the dedicated production crew, high-end broadcast or photography equipment, and the final deliverable (private viewing links or digital photo galleries). Be sure to check what each tier entails during your selection—we never include surprise logistical costs." }
      },
      {
        "@type": "Question",
        "name": "Do we need to pay for the crew's transport if the burial is outside Nairobi?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes. Our standard packages cater to Nairobi environs. However, our media crews regularly cover burials across Kenya (e.g., Western, Nyanza, Central). The calculator automatically estimates transport fees based on your inputted distance from the Nairobi CBD so your committee can plan the exact budget." }
      },
      {
        "@type": "Question",
        "name": "How do diaspora families coordinate and pay for funeral media remotely?",
        "acceptedAnswer": { "@type": "Answer", "text": "We serve as your trusted, on-the-ground eyes. You can calculate costs online, chat with us to confirm deployment, and manage your 75% deposit remotely. This ensures family members in the UK, USA, or Canada can attend securely without logistical stress." }
      },
      {
        "@type": "Question",
        "name": "Do you require full payment upfront?",
        "acceptedAnswer": { "@type": "Answer", "text": "No. We understand that funeral budgets in Kenya are extensive. We only require a 75% deposit to lock in your date and secure our specialized crew. The remaining 25% balance is due upon successful execution of the livestream or delivery of the images." }
      },
      {
        "@type": "Question",
        "name": "Is drone aerial videography necessary for a funeral?",
        "acceptedAnswer": { "@type": "Answer", "text": "While optional, 4K drone coverage is highly recommended for capturing the monumental scale of large gatherings, car processions, and the graveside landscape—especially important for high-profile burials or maintaining visual records of ancestral homesteads." }
      },
      {
        "@type": "Question",
        "name": "How fast can we get the livestream broadcast link to share with relatives?",
        "acceptedAnswer": { "@type": "Answer", "text": "As soon as your booking is confirmed and the deposit settled, we generate a private, dedicated YouTube, Twitch, or Facebook link. This allows your family to test and distribute the viewing URL in WhatsApp groups days before the actual service begins." }
      }
    ]
  };

  if (isSuccess) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="card-default p-12 border-green/20 shadow-2xl"
        >
          <div className="w-20 h-20 bg-green/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 size={40} className="text-green" />
          </div>
          <h2 className="text-4xl font-serif text-ink mb-6">Booking Request <em>Sent!</em></h2>
          <p className="text-xl text-ink2 mb-10 max-w-2xl mx-auto leading-relaxed">
            Thank you for trusting Faraja Media. We have received your request and will contact you within 2 hours to confirm availability and provide a full quote.
          </p>
          <div className="bg-surface p-8 rounded-[2rem] border border-border-subtle mb-10 text-left max-w-md mx-auto">
            <h3 className="font-serif text-xl text-ink mb-4 flex items-center gap-2">
              <CreditCard size={18} className="text-gold" />
              Payment Details (75% Deposit)
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-ink2">M-Pesa Paybill:</span>
                <span className="font-mono font-bold text-ink">247247</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink2">Account Number:</span>
                <span className="font-mono font-bold text-ink">502401</span>
              </div>
              <div className="pt-3 border-t border-border-subtle flex justify-between text-gold font-bold">
                <span>Deposit Amount:</span>
                <span className="price-figure text-xl">KSh {Math.round(total * 0.75).toLocaleString()}</span>
              </div>
            </div>
          </div>
          <p className="text-sm text-ink3 mb-6 italic">No payment is taken now — we confirm availability first, then invoice.</p>

          {/* ══════════ VISIBLE FAQ SECTION ══════════ */}
          <div className="max-w-4xl mx-auto py-8 mb-8 text-left border-t border-white/5">
            <h2 className="text-2xl font-serif text-ink mb-6">Frequently Asked Questions about the Faraja Funeral Media Calculator</h2>
            <div className="space-y-3">
              {faqSchemaData.mainEntity.map((faq, i) => (
                <details key={i} className="group border border-white/5 bg-card-bg rounded-2xl overflow-hidden [&_summary::-webkit-details-marker]:hidden">
                  <summary className="cursor-pointer p-6 font-bold text-ink hover:text-gold transition-colors flex justify-between items-center outline-none">
                    <h3 className="text-lg font-serif m-0 pr-4">{faq.name}</h3>
                    <span className="text-gold group-open:rotate-180 transition-transform duration-300">
                      <ChevronRight size={20} className="rotate-90 group-open:-rotate-90" />
                    </span>
                  </summary>
                  <div className="p-6 pt-0 text-sm text-ink2 leading-relaxed border-t border-white/5">
                    {faq.acceptedAnswer.text}
                  </div>
                </details>
              ))}
            </div>
          </div>

          <button onClick={() => window.location.href = '/'} className="text-gold font-bold hover:underline tracking-widest uppercase text-xs">
            Return to Homepage
          </button>
        </motion.div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // MAIN LAYOUT
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className={cn('w-full', step === 1 && 'pb-24 md:pb-0')}>
      <Helmet>
        {/* Technical SEO */}
        <title>Free Funeral Media Cost Calculator | Faraja Media – Plan with Dignity</title>
        <meta name="description" content="Estimate your funeral media costs instantly including livestreaming, photography, and cinematic videos in Nairobi. Safe, respectful planning for diaspora families." />
        <meta name="keywords" content="funeral media cost calculator, funeral photography price, live streaming cost Kenya, funeral video calculator, memorial media estimator, diaspora funeral planning" />
        <link rel="canonical" href="https://farajamedia.com/booking" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://farajamedia.com/booking" />
        <meta property="og:title" content="Free Funeral Media Cost Calculator" />
        <meta property="og:description" content="Estimate your funeral media costs instantly – no payment needed." />
        <meta property="og:image" content="https://farajamedia.com/og-booking.jpg" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Free Funeral Media Cost Calculator" />
        <meta name="twitter:description" content="Plan a funeral with clarity – free cost estimator." />
        <meta name="twitter:image" content="https://farajamedia.com/og-booking.jpg" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchemaData)}
        </script>
      </Helmet>



      {/* --- TRUST BAR --- */}
      <div className="border-y border-gold/12 bg-deep/80 backdrop-blur-md relative z-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-center gap-3 text-gold/80">
              <ShieldCheck size={20} className="text-gold" />
              <span className="text-xs uppercase tracking-widest font-bold">Safe & Respectful</span>
            </div>
            <div className="flex items-center gap-3 text-gold/80 border-l border-white/5 pl-6">
              <Clock size={20} className="text-gold" />
              <span className="text-xs uppercase tracking-widest font-bold">2-Hour Rapid Response</span>
            </div>
            <div className="flex items-center gap-3 text-gold/80 border-l border-white/5 pl-6">
              <Star size={20} className="text-gold" />
              <span className="text-xs uppercase tracking-widest font-bold">500+ Memorials Filmed</span>
            </div>
            <div className="hidden md:flex items-center gap-2 px-3 py-1 bg-gold/8 rounded-full border border-gold/25">
              <span className="w-1 h-1 rounded-full bg-gold animate-pulse" />
              <span className="text-[9px] uppercase tracking-widest font-black text-gold">Verified Kenyan Business</span>
            </div>

            <button
              onClick={toggleQuietDignity}
              className={cn(
                "hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all",
                quietDignity ? "bg-bg border-white/20 text-ink2" : "bg-bg/50 border-white/5 text-ink3 hover:text-ink2 hover:border-white/10"
              )}
            >
              <div className={cn("w-2 h-2 rounded-full", quietDignity ? "bg-green" : "bg-white/20")} />
              <span className="text-[9px] uppercase tracking-widest font-bold">Quiet Dignity {quietDignity ? 'Active' : 'Off'}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12 lg:py-24">
        <div className="grid lg:grid-cols-3 gap-12">

          {/* ── MAIN COLUMN ── */}
          <div className="lg:col-span-2 space-y-12">

            {/* Before you begin block */}
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-2xl bg-gold/5 border border-gold/15 relative overflow-hidden group mb-12"
              >
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <blockquote className="text-sm md:text-base font-serif text-ink italic leading-relaxed max-w-2xl">
                    "Take your time. There is no rush here. Build the coverage that feels right for your family — and we will take care of everything."
                  </blockquote>
                  <div className="flex gap-2 flex-wrap md:flex-nowrap">
                    {['🕯️ Trusted since 2009', '📍 Nairobi & Kenya', '🌍 Diaspora-ready'].map((chip, i) => (
                      <span key={i} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[9px] uppercase tracking-widest font-bold text-ink3 whitespace-nowrap">
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="absolute top-0 right-0 w-24 h-24 bg-gold/5 blur-2xl group-hover:bg-gold/10 transition-colors" />
              </motion.div>
            )}

            {/* AI Referral Banner */}
            {step === 1 && (
              <div className="mb-8 p-4 rounded-xl bg-gold/5 border border-gold/20 flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <HelpCircle className="text-gold" size={20} />
                  <span className="text-ink2 text-sm">Have questions? <strong className="text-ink">Ask Faraja AI</strong> – she speaks English & Swahili.</span>
                </div>
                <button
                  onClick={() => {
                    const aiButton = document.querySelector('[data-ai-trigger]') as HTMLButtonElement;
                    if (aiButton) aiButton.click();
                    else window.dispatchEvent(new CustomEvent('open-ai-assistant'));
                  }}
                  className="px-4 py-2 rounded-full bg-gold text-bg text-xs font-bold uppercase tracking-wider hover:bg-gold-hover transition"
                >
                  Chat Now
                </button>
              </div>
            )}

            <h1 className="text-3xl font-serif text-ink tracking-tight mb-6">
              {step === 1 ? 'Build Your Coverage' : step === 2 ? 'Your Details' : 'Final Quotation'}
            </h1>

            <div className="flex items-center gap-3 mb-6 flex-wrap">
              {[
                { n: 1, label: 'Coverage' },
                { n: 2, label: 'Details' },
                { n: 3, label: 'Confirm' },
              ].map((s, i, arr) => (
                <div key={s.n} className="flex items-center gap-2">
                  <div className={cn(
                    'w-9 h-9 rounded-full border-2 flex items-center justify-center font-bold text-xs transition-all',
                    step === s.n
                      ? 'border-gold bg-gold-bg text-gold shadow-[0_0_15px_rgba(201,168,76,0.15)]'
                      : step > s.n
                        ? 'bg-green border-green text-ink'
                        : 'border-ink/12 bg-transparent text-ink3'
                  )}>
                    {step > s.n ? <Check size={14} strokeWidth={3} /> : s.n}
                  </div>
                  <span className={cn(
                    'text-[10px] uppercase tracking-widest font-bold',
                    step === s.n ? 'text-gold' : step > s.n ? 'text-green' : 'text-ink3'
                  )}>
                    {s.label}
                  </span>
                  {i < arr.length - 1 && (
                    <div className={cn(
                      'hidden md:block w-12 h-px transition-colors',
                      step > s.n ? 'bg-green/40' : 'bg-white/10'
                    )} />
                  )}
                </div>
              ))}
            </div>

            <p className="text-[10px] uppercase tracking-widest font-bold mb-12 text-ink3 font-medium">
              {step === 1 && 'Almost there — just 2 more steps'}
              {step === 2 && 'Final step — review your quotation'}
              {step === 3 && 'Everything looks perfect'}
            </p>

            <AnimatePresence mode="wait">

              {/* ══════════ STEP 1 ══════════ */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-8"
                >

                  {/* Crowd size */}
                  <div className="card-default p-8 space-y-6">
                    <div className="flex items-center gap-4 mb-2">
                      <div className="p-3 bg-white/5 rounded-2xl text-gold"><Users size={20} /></div>
                      <div>
                        <h2 className="text-xl font-serif text-ink">How large is the <em>gathering?</em></h2>
                        <p className="text-sm text-ink2">Determines camera coverage — all included in price</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {CROWD_OPTIONS.map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => updateSel({ crowd: opt.id as 's' | 'm' | 'l' })}
                          className={cn(
                            'p-6 rounded-2xl border text-left transition-all duration-500 relative overflow-hidden',
                            sel.crowd === opt.id
                              ? 'bg-gold-bg border-gold'
                              : 'bg-white/[0.02] border-white/5 hover:border-white/10'
                          )}
                        >
                          <div className={cn('text-2xl font-serif mb-1', sel.crowd === opt.id ? 'text-gold' : 'text-ink')}>{opt.range}</div>
                          <div className="eyebrow !mb-2">{opt.label}</div>
                          <div className="text-xs text-ink3 leading-relaxed">{opt.cameras}</div>
                          {sel.crowd === opt.id && <Check size={16} className="text-gold absolute top-4 right-4" />}
                        </button>
                      ))}
                    </div>
                    {sel.crowd !== 's' && (
                      <div className="text-xs text-ink3 bg-white/[0.02] rounded-xl px-4 py-3 border border-white/5 italic">
                        💡 {CROWD_OPTIONS.find(o => o.id === sel.crowd)?.rationale}
                      </div>
                    )}
                  </div>

                  {/* Services */}
                  <ServiceSection
                    title="Photography"
                    icon={<Camera size={24} />}
                    description="High-resolution memorial photography with rapid 48-hour delivery."
                    items={PHOTO_ITEMS}
                    selected={sel.photoOpt}
                    onSelect={key => updateSel({ photoOpt: sel.photoOpt === key ? '' : key })}
                    addonItems={PHOTO_ADDON_ITEMS}
                    selectedAddons={sel.photoAddons}
                    onToggleAddon={key => updateSel({ photoAddons: toggleList(sel.photoAddons, key) })}
                    hasSecondCam={sel.photoSecondCam}
                    onToggleSecondCam={() => toggleCam2Manual('photo')}
                    secondCamRecommended={sel.crowd !== 's'}
                    hasThirdCam={thirdCam.photo}
                    onToggleThirdCam={() => toggleCam3Manual('photo')}
                    upsellNote="Add a second camera and get +50 more images for your gallery."
                    skipped={svcSkipped.photo}
                    onSkip={() => toggleSkip('photo')}
                  />

                  <ServiceSection
                    title="Live Streaming"
                    icon={<Globe size={24} />}
                    description="Connect your global family with reliable, high-definition streaming."
                    items={STREAM_ITEMS}
                    selected={sel.streamOpt}
                    onSelect={key => updateSel({ streamOpt: sel.streamOpt === key ? '' : key })}
                    addonItems={STREAM_ADDON_ITEMS}
                    selectedAddons={sel.streamAddons}
                    onToggleAddon={key => updateSel({ streamAddons: toggleList(sel.streamAddons, key) })}
                    hasSecondCam={sel.streamSecondCam}
                    onToggleSecondCam={() => toggleCam2Manual('stream')}
                    secondCamRecommended={sel.crowd !== 's'}
                    hasThirdCam={thirdCam.stream}
                    onToggleThirdCam={() => toggleCam3Manual('stream')}
                    upsellNote="Add professional editing to your livestream recording for KSh 5,000."
                    skipped={svcSkipped.stream}
                    onSkip={() => toggleSkip('stream')}
                  />

                  <ServiceSection
                    title="Cinematic Video"
                    icon={<Video size={24} />}
                    description="Cinema-grade memorial films that preserve your loved one's legacy."
                    items={VIDEO_ITEMS}
                    selected={sel.videoOpt}
                    onSelect={key => updateSel({ videoOpt: sel.videoOpt === key ? '' : key, videoEdit: '' })}
                    addonItems={VIDEO_ADDON_ITEMS}
                    selectedAddons={sel.videoAddons}
                    onToggleAddon={key => updateSel({ videoAddons: toggleList(sel.videoAddons, key) })}
                    hasSecondCam={sel.videoSecondCam}
                    onToggleSecondCam={() => toggleCam2Manual('video')}
                    secondCamRecommended={sel.crowd !== 's'}
                    hasThirdCam={thirdCam.video}
                    onToggleThirdCam={() => toggleCam3Manual('video')}
                    skipped={svcSkipped.video}
                    onSkip={() => toggleSkip('video')}
                    extraSection={
                      <div className="space-y-4">
                        {sel.videoOpt === 'viF' && (
                          <button onClick={() => setShowLegacyModal(true)} className="w-full p-4 bg-white/5 hover:bg-gold/10 border border-gold/20 rounded-xl flex justify-between items-center text-xs text-gold transition-colors mt-2">
                            <span className="font-bold flex items-center gap-2"><Video size={14} /> Preview Legacy Output</span>
                            <ArrowRight size={14} />
                          </button>
                        )}
                        {sel.videoOpt && (
                          <div className="pt-4 border-t border-border-subtle mt-4">
                            <p className="text-xs text-ink3 mb-3 uppercase tracking-widest font-bold">Film Edit Length</p>
                            <div className="grid md:grid-cols-3 gap-3">
                              {VIDEO_EDIT_ITEMS.map(item => (
                                <button
                                  key={item.key}
                                  onClick={() => updateSel({ videoEdit: sel.videoEdit === item.key ? '' : item.key })}
                                  className={cn(
                                    'p-3 rounded-xl border text-left transition-all',
                                    sel.videoEdit === item.key
                                      ? 'bg-gold-bg border-gold'
                                      : 'bg-bg border-border-subtle hover:border-border-raised'
                                  )}
                                >
                                  <div className="text-[10px] font-bold uppercase tracking-widest text-ink3 mb-1">
                                    {item.tag || 'Upgrade'}
                                  </div>
                                  <div className="font-bold text-sm text-ink mb-1">{item.name}</div>
                                  <div className="text-gold font-serif">
                                    {item.price === 0 ? 'Included' : quietDignity ? 'Private Upgrade' : `KSh ${item.price.toLocaleString()}`}
                                  </div>
                                  <div className="text-[10px] text-ink3 mt-1 leading-tight">{item.desc}</div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    }
                  />

                  <ServiceSection
                    title="Drone Aerial Coverage"
                    icon={<Zap size={24} />}
                    description="Dignified aerial perspectives of the procession and ceremony."
                    items={DRONE_ITEMS}
                    selected={sel.droneOpt}
                    onSelect={key => updateSel({ droneOpt: sel.droneOpt === key ? '' : key })}
                    addonItems={DRONE_ADDON_ITEMS}
                    selectedAddons={sel.droneAddons}
                    onToggleAddon={key => updateSel({ droneAddons: toggleList(sel.droneAddons, key) })}
                    skipped={svcSkipped.drone}
                    onSkip={() => toggleSkip('drone')}
                  />

                  {/* ── TRANSPORT ── */}
                  <div className="card-default p-8 space-y-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-white/5 rounded-2xl text-gold"><Car size={20} /></div>
                      <div>
                        <h3 className="text-xl font-serif text-ink">Transport <em>Estimate</em></h3>
                        <p className="text-sm text-ink2">Crew travel from Westlands base · shown separately from service total</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {TRANSPORT_ZONES.map(zone => (
                        <button
                          key={zone.key}
                          onClick={() => updateSel({ transportZone: sel.transportZone === zone.key ? '' : zone.key, transportKm: 0 })}
                          className={cn(
                            'p-5 rounded-2xl border text-left transition-all duration-300 relative',
                            sel.transportZone === zone.key
                              ? 'bg-gold-bg border-gold'
                              : 'bg-white/[0.02] border-white/5 hover:border-white/10'
                          )}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className={cn('font-bold text-sm mb-1', sel.transportZone === zone.key ? 'text-gold' : 'text-ink')}>{zone.label}</div>
                              <div className="text-[11px] text-ink3 leading-relaxed">{zone.desc}</div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              {zone.fee !== null
                                ? <span className="text-gold font-serif text-sm">{quietDignity ? 'Private' : `KSh ${zone.fee.toLocaleString()}`}</span>
                                : <span className="text-gold font-serif text-sm">{quietDignity ? 'Private' : `KSh ${TRANSPORT_PER_KM}/km`}</span>
                              }
                            </div>
                          </div>
                          {sel.transportZone === zone.key && <Check size={14} className="text-gold absolute top-3 right-3" />}
                        </button>
                      ))}
                    </div>

                    {/* Outside Nairobi km input */}
                    {sel.transportZone === 'outside' && (
                      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                        <label className="text-[10px] text-ink3 block uppercase tracking-[0.2em] font-bold">
                          Distance from Nairobi CBD (one way, km)
                        </label>
                        <div className="flex items-center gap-4">
                          <input
                            type="number"
                            min={1}
                            max={500}
                            placeholder="e.g. 45"
                            value={sel.transportKm || ''}
                            onChange={e => updateSel({ transportKm: Math.max(0, parseInt(e.target.value) || 0) })}
                            className="input-default w-40 text-center text-lg font-bold"
                            onFocus={(e) => { e.target.style.border = `1.5px solid var(--gold)`; e.target.style.boxShadow = `0 0 0 3px rgba(201,168,76,0.10)`; }}
                            onBlur={(e) => { e.target.style.border = `1.5px solid var(--border)`; e.target.style.boxShadow = 'none'; }}
                          />
                          <span className="text-ink3 text-sm">km</span>
                          {sel.transportKm > 0 && (
                            <div className="flex-1 text-right">
                              <span className="text-gold font-serif text-xl">KSh {calcTransport(sel).toLocaleString()}</span>
                              <p className="text-[10px] text-ink3 mt-1">{sel.transportKm} km × KSh {TRANSPORT_PER_KM} · min KSh {TRANSPORT_OUTSIDE_MIN.toLocaleString()}</p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}

                    {/* Transport summary */}
                    {sel.transportZone && calcTransport(sel) > 0 && (
                      <div className="flex items-center justify-between p-4 bg-white/[0.02] rounded-xl border border-white/5 text-sm">
                        <div className="flex items-center gap-2 text-ink3">
                          <MapPin size={14} className="text-gold" aria-hidden="true" />
                          <span>Transport estimate</span>
                        </div>
                        <span className="text-gold font-serif font-bold">+ KSh {calcTransport(sel).toLocaleString()}</span>
                      </div>
                    )}

                    {!sel.transportZone && (
                      <p className="text-xs text-ink3 italic">Skip this if you prefer Faraja Media to quote transport separately after booking.</p>
                    )}
                  </div>

                </motion.div>
              )}

              {/* ══════════ STEP 2 ══════════ */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-8"
                >
                  {errors.service && (
                    <div className="p-4 bg-red/10 border border-red/20 rounded-xl text-xs text-red font-bold uppercase tracking-widest flex items-center justify-between">
                      <span>⚠ {errors.service}</span>
                      <button onClick={() => setStep(1)} className="underline ml-4">← Back to services</button>
                    </div>
                  )}

                  <div className="card-default p-8">
                    <span className="eyebrow">Essential Information</span>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Input id="firstName" label="First Name" placeholder="e.g. Margaret" required autoComplete="given-name" value={form.firstName} onChange={(e: any) => updateForm({ firstName: e.target.value })} error={errors.firstName} />
                      <Input id="lastName" label="Last Name" placeholder="e.g. Wanjiku" required autoComplete="family-name" value={form.lastName} onChange={(e: any) => updateForm({ lastName: e.target.value })} error={errors.lastName} />
                      <Input id="whatsapp" ref={whatsappRef} label="WhatsApp Number" placeholder="+254 7XX XXX XXX" required autoComplete="tel" value={form.whatsapp} onChange={(e: any) => updateForm({ whatsapp: e.target.value })} error={errors.whatsapp} />
                      <Input id="email" ref={emailRef} label="Email Address" placeholder="your@email.com" type="email" required autoComplete="email" value={form.email} onChange={(e: any) => updateForm({ email: e.target.value })} error={errors.email} />
                      <div className="md:col-span-2">
                        <Input id="deceased" label="Full Name of the Deceased" placeholder="e.g. John Kamau Mwangi" required value={form.deceased} onChange={(e: any) => updateForm({ deceased: e.target.value })} error={errors.deceased} />
                      </div>
                      <div>
                        <Input id="serviceDate" label="Service Date" type="date" required value={form.serviceDate} onChange={(e: any) => updateForm({ serviceDate: e.target.value })} error={errors.serviceDate} min={tomorrow()} />
                        <AnimatePresence mode="wait">
                          {dateStatus === 'none' && (
                            <motion.div key="none" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, height: 0 }} className="p-3 bg-white/[0.02] border border-white/5 rounded-lg text-xs leading-relaxed mt-3 flex items-start gap-2">
                              <Clock size={14} className="text-gold flex-shrink-0 mt-0.5" />
                              <span className="text-ink3 italic">Select a date to check availability. We limit our team to 3-4 families per week.</span>
                            </motion.div>
                          )}
                          {dateStatus === 'checking' && (
                            <motion.div key="checking" initial={{ opacity: 0, height: 0, marginTop: 0 }} animate={{ opacity: 1, height: 'auto', marginTop: 12 }} exit={{ opacity: 0, height: 0, marginTop: 0 }} className="flex items-center gap-2 text-xs text-gold overflow-hidden">
                              <div className="w-3 h-3 border-2 border-gold border-t-transparent rounded-full animate-spin flex-shrink-0" />
                              Checking availability for this date...
                            </motion.div>
                          )}
                          {dateStatus === 'available' && (
                            <motion.div key="available" initial={{ opacity: 0, height: 0, marginTop: 0 }} animate={{ opacity: 1, height: 'auto', marginTop: 12 }} className="p-3 bg-green-bg/50 border border-green/20 rounded-lg text-xs leading-relaxed overflow-hidden shadow-lg shadow-green/5">
                              <div className="flex items-center gap-2 text-green font-bold mb-1 uppercase tracking-widest">
                                <CheckCircle2 size={12} /> Date Available
                              </div>
                              <span className="text-ink2 block mt-2">We dedicate our team to only 3-4 families a week. Currently, <strong className="text-white">1-2 slots remain</strong> around this date.</span>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                      <Input id="startTime" label="Start Time" type="time" required value={form.startTime} onChange={(e: any) => updateForm({ startTime: e.target.value })} error={errors.startTime} />
                      <div className="space-y-2">
                        <Input id="endTime" label="End Time (approx.)" type="time" required value={form.endTime} onChange={(e: any) => updateForm({ endTime: e.target.value })} error={errors.endTime} />
                        {form.startTime && (
                          <div className="px-3 py-2 bg-gold/5 border border-gold/10 rounded-lg">
                            <p className="text-[10px] text-ink2 leading-relaxed">
                              <span className="text-gold font-bold">Overtime Policy:</span> Included duration is up to <span className="text-ink font-bold">{Math.max(...[sel.photoOpt, sel.streamOpt, sel.videoOpt, sel.droneOpt].filter(Boolean).map(t => TIER_HOURS[t] || 0))} hours</span>. Manual extensions incur overtime fees.
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="md:col-span-2">
                        <Input id="venue" label="Primary Venue" placeholder="e.g. St. Andrew's Church, Westlands, Nairobi" required value={form.venue} onChange={(e: any) => updateForm({ venue: e.target.value })} error={errors.venue} />
                      </div>
                      <div className="md:col-span-2">
                        <label htmlFor="relation" className="text-xs text-ink3 block mb-2 uppercase tracking-[0.2em] font-bold">
                          Your Relationship to the Deceased <span className="text-gold">*</span>
                        </label>
                        <select
                          id="relation"
                          value={form.relation}
                          onChange={(e: any) => updateForm({ relation: e.target.value })}
                          className={cn(
                            'input-default w-full min-h-[48px]',
                            errors.relation ? 'border-red/50 bg-red/5' : ''
                          )}
                        >
                          <option value="">— Select —</option>
                          {['Son', 'Daughter', 'Spouse / Partner', 'Sibling', 'Parent', 'Grandchild', 'Extended family member', 'Funeral home / coordinator', 'Church / religious official', 'Friend of the family', 'Other'].map(o => <option key={o}>{o}</option>)}
                        </select>
                        {errors.relation && <p className="text-[10px] text-red font-bold uppercase tracking-widest mt-2">{errors.relation}</p>}
                      </div>
                    </div>
                  </div>

                  {/* Diaspora toggle */}
                  <div
                    onClick={() => updateSel({ diaspora: !sel.diaspora })}
                    className={cn(
                      'flex items-center justify-between p-8 rounded-[2rem] border cursor-pointer transition-all duration-500',
                      sel.diaspora ? 'border-gold bg-gold-bg' : 'border-white/5 bg-white/[0.02] hover:border-white/10'
                    )}
                  >
                    <div>
                      <p className="font-bold text-ink text-sm uppercase tracking-widest">I am based outside Kenya (diaspora)</p>
                      <p className="text-xs text-ink3 mt-1 italic">Coordinating remotely · booking on behalf of family in Kenya</p>
                    </div>
                    <div className={cn(
                      'w-12 h-6 rounded-full relative transition-all duration-300 flex-shrink-0',
                      sel.diaspora ? 'bg-gold' : 'bg-white/10'
                    )}>
                      <div className={cn('absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all duration-300', sel.diaspora ? 'left-7' : 'left-1')} />
                    </div>
                  </div>

                  {sel.diaspora && (
                    <div className="grid md:grid-cols-2 gap-6 card-default p-8">
                      <div>
                        <label htmlFor="diasporaCountry" className="text-xs text-ink3 block mb-2 uppercase tracking-[0.2em] font-bold">Country of Residence</label>
                        <select id="diasporaCountry" value={form.diasporaCountry} onChange={(e: any) => updateForm({ diasporaCountry: e.target.value })} className="input-default w-full min-h-[48px]">
                          <option value="">— Select country —</option>
                          {['United Kingdom', 'United States', 'Canada', 'Australia', 'Germany', 'Netherlands', 'South Africa', 'Other'].map(o => <option key={o}>{o}</option>)}
                        </select>
                      </div>
                      <Input id="kenyaContact" label="Kenya Ground Contact (optional)" placeholder="Name and WhatsApp of local contact" value={form.kenyaContact} onChange={(e: any) => updateForm({ kenyaContact: e.target.value })} />
                    </div>
                  )}

                  {/* Optional details */}
                  <div className="card-default p-8 space-y-6">
                    <span className="eyebrow">Optional Details — helps us serve your family better</span>
                    <Input id="venue2" label="Second Location (optional)" placeholder="e.g. Langata Cemetery — graveside at 2:00 PM" value={form.venue2} onChange={(e: any) => updateForm({ venue2: e.target.value })} />
                    <div>
                      <label htmlFor="keyMoments" className="text-xs text-ink3 block mb-2 uppercase tracking-[0.2em] font-bold">Key Moments to Capture</label>
                      <textarea
                        id="keyMoments"
                        className="input-default w-full h-32 text-sm"
                        placeholder="e.g. Family choir at 10:30 AM · Grandchildren reading the eulogy at 11:15 AM"
                        value={form.keyMoments} onChange={(e: any) => updateForm({ keyMoments: e.target.value })}
                        onFocus={(e) => {
                          e.target.style.border = `1.5px solid var(--gold)`;
                          e.target.style.boxShadow = `0 0 0 3px rgba(201,168,76,0.10)`;
                        }}
                        onBlur={(e) => {
                          e.target.style.border = `1.5px solid var(--border)`;
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-ink3 block mb-2 uppercase tracking-[0.2em] font-bold">Additional Notes / Special Requests</label>
                      <textarea className="input-default w-full h-32 text-sm"
                        placeholder="Cultural/religious notes, specific instructions, anything else that will help us serve your family well"
                        value={form.notes} onChange={(e: any) => updateForm({ notes: e.target.value })}
                        onFocus={(e) => {
                          e.target.style.border = `1.5px solid var(--gold)`;
                          e.target.style.boxShadow = `0 0 0 3px rgba(201,168,76,0.10)`;
                        }}
                        onBlur={(e) => {
                          e.target.style.border = `1.5px solid var(--border)`;
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-ink3 block mb-2 uppercase tracking-[0.2em] font-bold">
                        How did you find us? (optional)
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {['Google Search', 'Facebook', 'Instagram', 'Funeral Home', 'Family Referral', 'Past Client', 'WhatsApp', 'Other'].map(v => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => updateForm({ refOther: form.refOther === v ? '' : v })}
                            className={cn(
                              'px-3 py-2 rounded-lg border text-xs transition-all min-h-[44px]',
                              form.refOther === v
                                ? 'bg-gold/10 border-gold text-gold font-bold'
                                : 'bg-white/[0.02] border-white/5 text-ink3 hover:border-white/10'
                            )}
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                </motion.div>
              )}

              {/* ══════════ STEP 3: FINAL REVIEW (FORMAL QUOTATION) ══════════ */}
              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-12"
                >
                  <div className="card-default p-8 lg:p-12 bg-deep border-gold/15 shadow-2xl relative overflow-hidden">
                    {/* Summary accent gradient */}
                    <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-gold via-gold/20 to-transparent" />

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12 pb-12 border-b border-white/5">
                      <div>
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center text-gold border border-gold/20 shadow-[0_0_15px_rgba(201,168,76,0.15)]">
                            <CheckCircle2 size={24} />
                          </div>
                          <h2 className="text-4xl font-serif text-ink italic leading-none">Formal Quotation</h2>
                        </div>
                        <p className="text-ink3 text-sm flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
                          Generated on {new Date().toLocaleDateString('en-KE', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                      </div>
                      <div className="text-right hidden md:block">
                        <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-ink2/50 mb-1">Faraja Media — Official</p>
                        <p className="text-[9px] text-ink3 font-medium">Ref: {form.firstName.slice(0, 3).toUpperCase()}-{Math.floor(Math.random() * 900) + 100}</p>
                      </div>
                    </div>

                    <div className="space-y-12 mb-12">
                      {/* SECTION: CLIENT & EVENT */}
                      <div className="grid md:grid-cols-2 gap-12">
                        <div className="space-y-6">
                          <div className="flex items-center justify-between">
                            <span className="eyebrow !mb-0 text-gold/60">Client Information</span>
                            <button onClick={() => setStep(2)} className="text-[9px] uppercase tracking-widest font-bold text-gold hover:underline">Edit Hub</button>
                          </div>
                          <div className="grid grid-cols-2 gap-y-4 gap-x-2 text-xs">
                            <span className="text-ink3 uppercase tracking-tighter">Primary Contact</span>
                            <span className="text-ink font-bold text-right">{form.firstName} {form.lastName}</span>

                            <span className="text-ink3 uppercase tracking-tighter">WhatsApp</span>
                            <span className="text-ink font-bold text-right">{form.whatsapp}</span>

                            <span className="text-ink3 uppercase tracking-tighter">Email Address</span>
                            <span className="text-ink font-bold text-right truncate">{form.email}</span>

                            <span className="text-ink3 uppercase tracking-tighter">Relationship</span>
                            <span className="text-ink font-bold text-right">{form.relation}</span>
                          </div>
                        </div>

                        <div className="space-y-6">
                          <div className="flex items-center justify-between">
                            <span className="eyebrow !mb-0 text-gold/60">Event Logistics</span>
                            <button onClick={() => setStep(2)} className="text-[9px] uppercase tracking-widest font-bold text-gold hover:underline">Edit Details</button>
                          </div>
                          <div className="grid grid-cols-2 gap-y-4 gap-x-2 text-xs">
                            <span className="text-ink3 uppercase tracking-tighter">Memorial Date</span>
                            <span className="text-ink font-bold text-right">
                              {form.serviceDate ? new Date(form.serviceDate).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' }) : '---'}
                            </span>

                            <span className="text-ink3 uppercase tracking-tighter">Scheduled Time</span>
                            <span className="text-ink font-bold text-right">{form.startTime} — {form.endTime || 'Flexible'}</span>

                            <span className="text-ink3 uppercase tracking-tighter">Primary Venue</span>
                            <span className="text-ink font-bold text-right truncate">{form.venue}</span>

                            <span className="text-ink3 uppercase tracking-tighter">Gathering Size</span>
                            <span className="text-ink font-bold text-right">{CROWD_LABEL[sel.crowd].split('(')[0]}</span>
                          </div>
                        </div>
                      </div>

                      {/* SECTION: SERVICES TABLE */}
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <span className="eyebrow !mb-0 text-gold/60">Selected Services</span>
                          <button onClick={() => setStep(1)} className="text-[9px] uppercase tracking-widest font-bold text-gold hover:underline">Change Coverage</button>
                        </div>
                        <div className="border border-white/5 rounded-2xl overflow-hidden">
                          <table className="w-full text-xs text-left border-collapse">
                            <thead>
                              <tr className="bg-white/5 text-ink3 uppercase tracking-widest text-[9px] font-bold">
                                <th className="px-6 py-4 border-b border-white/5">Description of Service</th>
                                <th className="px-6 py-4 border-b border-white/5 text-right w-32">Amount</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                              {/* Photography */}
                              {sel.photoOpt && (
                                <>
                                  <tr>
                                    <td className="px-6 py-4">
                                      <p className="text-ink font-bold">Photography: {PHOTO_ITEMS.find(i => i.key === sel.photoOpt)?.name}</p>
                                      <p className="text-ink3 text-[10px] mt-1">{sel.photoSecondCam ? 'Includes 2nd Camera (+50 images)' : 'Standard Single Camera'}</p>
                                    </td>
                                    <td className="px-6 py-4 text-right font-mono text-gold">{fmt(PHOTO_BASE[sel.photoOpt as keyof typeof PHOTO_BASE] + (sel.photoSecondCam ? CAM2 : 0) + (sel.crowd === 'l' ? CAM2 : 0))}</td>
                                  </tr>
                                  {sel.photoAddons.map(k => (
                                    <tr key={k}>
                                      <td className="px-6 py-2 text-ink2 pl-10">— {PHOTO_ADDON_ITEMS.find(i => i.key === k)?.name}</td>
                                      <td className="px-6 py-2 text-right font-mono text-ink3">{fmt(PHOTO_ADDONS[k as keyof typeof PHOTO_ADDONS] || 0)}</td>
                                    </tr>
                                  ))}
                                </>
                              )}

                              {/* Streaming */}
                              {sel.streamOpt && (
                                <>
                                  <tr>
                                    <td className="px-6 py-4">
                                      <p className="text-ink font-bold">Streaming: {STREAM_ITEMS.find(i => i.key === sel.streamOpt)?.name}</p>
                                      <p className="text-ink3 text-[10px] mt-1">{sel.streamSecondCam ? 'Includes 2nd Camera' : 'Standard Broadcast'}</p>
                                    </td>
                                    <td className="px-6 py-4 text-right font-mono text-gold">{fmt(STREAM_BASE[sel.streamOpt as keyof typeof STREAM_BASE] + (sel.streamSecondCam ? CAM2 : 0) + (sel.crowd === 'l' ? CAM2 : 0))}</td>
                                  </tr>
                                  {sel.streamAddons.map(k => (
                                    <tr key={k}>
                                      <td className="px-6 py-2 text-ink2 pl-10">— {STREAM_ADDON_ITEMS.find(i => i.key === k)?.name}</td>
                                      <td className="px-6 py-2 text-right font-mono text-ink3">{fmt(STREAM_ADDONS[k as keyof typeof STREAM_ADDONS] || 0)}</td>
                                    </tr>
                                  ))}
                                </>
                              )}

                              {/* Video */}
                              {sel.videoOpt && (
                                <>
                                  <tr>
                                    <td className="px-6 py-4">
                                      <p className="text-ink font-bold">Cinematic Video: {VIDEO_ITEMS.find(i => i.key === sel.videoOpt)?.name}</p>
                                      <p className="text-ink3 text-[10px] mt-1">{VIDEO_EDIT_ITEMS.find(i => i.key === sel.videoEdit)?.name}</p>
                                    </td>
                                    <td className="px-6 py-4 text-right font-mono text-gold">{fmt(VIDEO_BASE[sel.videoOpt as keyof typeof VIDEO_BASE] + (sel.videoSecondCam ? CAM2 : 0) + (sel.crowd === 'l' ? CAM2 : 0) + (VIDEO_EDIT[sel.videoEdit as keyof typeof VIDEO_EDIT] || 0))}</td>
                                  </tr>
                                  {sel.videoAddons.map(k => (
                                    <tr key={k}>
                                      <td className="px-6 py-2 text-ink2 pl-10">— {VIDEO_ADDON_ITEMS.find(i => i.key === k)?.name}</td>
                                      <td className="px-6 py-2 text-right font-mono text-ink3">{fmt(VIDEO_ADDONS[k as keyof typeof VIDEO_ADDONS] || 0)}</td>
                                    </tr>
                                  ))}
                                </>
                              )}

                              {/* Drone */}
                              {sel.droneOpt && (
                                <>
                                  <tr>
                                    <td className="px-6 py-4">
                                      <p className="text-ink font-bold">Drone: {DRONE_ITEMS.find(i => i.key === sel.droneOpt)?.name}</p>
                                    </td>
                                    <td className="px-6 py-4 text-right font-mono text-gold">{fmt(DRONE_BASE[sel.droneOpt as keyof typeof DRONE_BASE])}</td>
                                  </tr>
                                  {sel.droneAddons.map(k => (
                                    <tr key={k}>
                                      <td className="px-6 py-2 text-ink2 pl-10">— {DRONE_ADDON_ITEMS.find(i => i.key === k)?.name}</td>
                                      <td className="px-6 py-2 text-right font-mono text-ink3">{fmt(DRONE_ADDONS[k as keyof typeof DRONE_ADDONS] || 0)}</td>
                                    </tr>
                                  ))}
                                </>
                              )}

                              {/* Transport */}
                              {sel.transportZone && (
                                <tr>
                                  <td className="px-6 py-4">
                                    <p className="text-ink font-bold">Logistics & Crew Transport</p>
                                    <p className="text-ink3 text-[10px] mt-1">{TRANSPORT_ZONES.find(i => i.key === sel.transportZone)?.label}</p>
                                  </td>
                                  <td className="px-6 py-4 text-right font-mono text-gold">{fmt(calcTransport(sel))}</td>
                                </tr>
                              )}

                              {/* Overtime */}
                              {ot.total > 0 && (
                                <tr className="bg-gold/5">
                                  <td className="px-6 py-4">
                                    <p className="text-ink font-bold">Overtime Coverage</p>
                                    <p className="text-ink3 text-[10px] mt-1">{ot.hours} hour(s) beyond package duration</p>
                                  </td>
                                  <td className="px-6 py-4 text-right font-mono text-gold">+ {fmt(ot.total)}</td>
                                </tr>
                              )}
                            </tbody>
                            <tfoot className="bg-white/5">
                              <tr className="text-lg font-serif">
                                <td className="px-6 py-6 text-ink italic">Gross Total Investment</td>
                                <td className="px-6 py-6 text-right text-gold font-bold">{fmt(total + calcTransport(sel))}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>

                      {/* SECTION: SUMMARY & DEPOSIT */}
                      <div className="grid md:grid-cols-2 gap-8 items-start">
                        <div className="p-6 bg-white/5 rounded-2xl border border-white/5 space-y-4">
                          <div className="flex items-center gap-2 text-gold">
                            <ShieldCheck size={16} />
                            <span className="text-[10px] uppercase font-bold tracking-widest">Client Commitment</span>
                          </div>
                          <p className="text-[11px] text-ink2 leading-relaxed italic">
                            Faraja Media is a boutique production agency. We limit our bookings to ensure absolute focus. Formal confirmation follows within 2 hours of sending this quotation.
                          </p>
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-ink3 uppercase tracking-widest text-[10px] font-bold">Required Deposit (75%)</span>
                            <span className="text-gold font-bold text-lg font-mono">{fmt((total + calcTransport(sel)) * 0.75)}</span>
                          </div>
                          <div className="flex justify-between items-center text-sm pb-3 border-b border-white/5">
                            <span className="text-ink3 uppercase tracking-widest text-[10px] font-bold">Balance (Due on Date)</span>
                            <span className="text-ink2 font-mono">{fmt((total + calcTransport(sel)) * 0.25)}</span>
                          </div>
                          <p className="text-[9px] text-ink3 text-right italic leading-snug pt-1">
                            Deposit locks availability and secures specialized creative leads. <br />
                            Paybill: <strong className="text-ink2">247247</strong> | Acc: <strong className="text-ink2">502401</strong>
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="pt-10 border-t border-white/5">
                      <label className="flex items-start gap-4 p-6 rounded-2xl border border-white/5 bg-white/2 cursor-pointer hover:bg-white/5 transition-all">
                        <input
                          type="checkbox"
                          checked={form.consent}
                          onChange={() => updateForm({ consent: !form.consent })}
                          className="mt-1 w-5 h-5 accent-gold"
                        />
                        <span className="text-[11px] text-ink2 leading-relaxed">
                          I confirm that the memorial details provided are correct. I understand that <strong className="text-ink text-xs underline underline-offset-4 decoration-gold/30">no payment is required now</strong>. Clicking the button below will notify our coordinators to verify availability and lock this slot.
                        </span>
                      </label>
                      {errors.consent && <p className="text-[10px] text-red font-bold uppercase tracking-widest mt-3 ml-12">⚠ Please confirm the details before sending</p>}
                    </div>
                  </div>

                  <div className="flex flex-col items-center gap-6">
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting || !form.consent}
                      className={cn(
                        "btn-primary w-full max-w-lg py-6 text-xl justify-center shadow-2xl relative overflow-hidden group",
                        (!form.consent || isSubmitting) && "opacity-50 grayscale cursor-not-allowed"
                      )}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-3">
                          <div className="w-5 h-5 border-2 border-bg border-t-transparent rounded-full animate-spin" />
                          Connecting to WhatsApp...
                        </span>
                      ) : (
                        <>
                          Confirm & Request via WhatsApp
                          <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </button>
                    <div className="flex items-center gap-2 text-[10px] text-ink3 uppercase tracking-[0.3em] font-bold">
                      <span className="w-1.5 h-1.5 rounded-full bg-green animate-pulse" />
                      Official Booking Channel Active
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Navigation */}
              <div className="flex justify-between items-center pt-12 mt-12 border-t border-white/5">
                {step > 1 && step <= 3 ? (
                  <button
                    onClick={() => { setStep(s => s - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="flex items-center gap-2 text-ink3 hover:text-gold transition-all duration-300 uppercase tracking-widest text-[10px] font-bold"
                  >
                    <ChevronLeft size={16} /> Back
                  </button>
                ) : <div />}

                {step < 3 && (
                  <button
                    onClick={() => {
                      if (step === 1) {
                        const anyService = !!(sel.photoOpt || sel.streamOpt || sel.videoOpt || sel.droneOpt);
                        if (!anyService) { setErrors({ service: 'Please select at least one service' }); return; }
                        setStep(2);
                      } else {
                        if (validate(false)) setStep(3);
                      }
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="btn-primary group min-h-[56px] flex items-center justify-center gap-3 px-12"
                  >
                    Continue to {step === 1 ? 'Details' : 'Review Quotation'}
                    <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                )}
              </div>
            </AnimatePresence>
          </div>

          {/* ── SIDEBAR ── */}
          < div className="space-y-6" >
            <div className="bg-deep p-8 lg:p-10 rounded-[32px] border border-gold/15 sticky top-24 shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden">
              {/* Sidebar gradient accent */}
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-gold/20 via-gold/5 to-transparent" />

              <div className="flex items-center gap-3 mb-8 pb-8 border-b border-white/5">
                <div className="flex text-gold text-xs tracking-tighter">★★★★★</div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-ink2">Trust & Dignity</p>
                  <p className="text-[9px] text-ink3 font-medium uppercase tracking-wider">Served 500+ Families Since 2009</p>
                </div>
              </div>

              <div className="mb-8 p-4 bg-gold-bg border border-gold/20 rounded-2xl flex items-start gap-3">
                <Clock size={16} className="text-gold flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-ink2 leading-relaxed">
                  <strong className="text-gold">Capacity Alert:</strong> We serve only 2 families per day to ensure absolute focus and respect.
                </p>
              </div>

              <h2 className="text-2xl font-serif text-ink mb-1">Live Quotation</h2>
              <p className="text-[10px] text-ink3 mb-8 uppercase tracking-[0.2em] font-black">Nairobi · Excl. Transport</p>

              <div className="space-y-6 mb-10 min-h-[60px]">
                {sel.photoOpt && (
                  <SidebarSection label="Photography" basePrice={PHOTO_BASE[sel.photoOpt as keyof typeof PHOTO_BASE]}
                    extras={[
                      ...sel.photoAddons.map(k => ({ name: PHOTO_ADDON_ITEMS.find(i => i.key === k)?.name || k, price: PHOTO_ADDONS[k as keyof typeof PHOTO_ADDONS] })),
                      ...(sel.photoSecondCam ? [{ name: '2nd Camera (+50 images)', price: CAM2 }] : []),
                      ...(thirdCam.photo && sel.photoSecondCam ? [{ name: '3rd Camera', price: CAM2 }] : []),
                    ]}
                  />
                )}
                {sel.streamOpt && (
                  <SidebarSection label="Live Streaming" basePrice={STREAM_BASE[sel.streamOpt as keyof typeof STREAM_BASE]}
                    extras={[
                      ...sel.streamAddons.map(k => ({ name: STREAM_ADDON_ITEMS.find(i => i.key === k)?.name || k, price: STREAM_ADDONS[k as keyof typeof STREAM_ADDONS] })),
                      ...(sel.streamSecondCam ? [{ name: '2nd Camera', price: CAM2 }] : []),
                      ...(thirdCam.stream && sel.streamSecondCam ? [{ name: '3rd Camera', price: CAM2 }] : []),
                    ]}
                  />
                )}
                {sel.videoOpt && (
                  <SidebarSection label="Cinematic Video" basePrice={VIDEO_BASE[sel.videoOpt as keyof typeof VIDEO_BASE]}
                    extras={[
                      ...(sel.videoEdit && VIDEO_EDIT[sel.videoEdit as keyof typeof VIDEO_EDIT] > 0 ? [{ name: VIDEO_EDIT_ITEMS.find(i => i.key === sel.videoEdit)?.name || sel.videoEdit, price: VIDEO_EDIT[sel.videoEdit as keyof typeof VIDEO_EDIT] }] : []),
                      ...sel.videoAddons.map(k => ({ name: VIDEO_ADDON_ITEMS.find(i => i.key === k)?.name || k, price: VIDEO_ADDONS[k as keyof typeof VIDEO_ADDONS] })),
                      ...(sel.videoSecondCam ? [{ name: '2nd Camera', price: CAM2 }] : []),
                      ...(thirdCam.video && sel.videoSecondCam ? [{ name: '3rd Camera', price: CAM2 }] : []),
                    ]}
                  />
                )}
                {sel.droneOpt && (
                  <SidebarSection label="Drone Aerial" basePrice={DRONE_BASE[sel.droneOpt as keyof typeof DRONE_BASE]}
                    extras={sel.droneAddons.map(k => ({ name: DRONE_ADDON_ITEMS.find(i => i.key === k)?.name || k, price: DRONE_ADDONS[k as keyof typeof DRONE_ADDONS] }))}
                  />
                )}

                {total === 0 && <p className="text-xs text-ink3 italic py-4">Please select a service to see pricing.</p>}
              </div>

              <div className="pt-8 border-t border-white/5 mb-10">
                <div className="flex justify-between items-end mb-2">
                  <span className="text-ink3 text-[10px] uppercase tracking-widest font-black">Estimated Total</span>
                  <span className="text-4xl text-gold font-serif">{quietDignity ? 'Contact Us' : `KSh ${total.toLocaleString()}`}</span>
                </div>
                <p className="text-[10px] text-ink3 italic tracking-wide">75% deposit required after confirmation.</p>
              </div>

              {sel.transportZone && calcTransport(sel) > 0 && (
                <div className="pt-6 border-t border-white/5 mb-8">
                  <div className="flex justify-between items-center">
                    <span className="text-ink3 text-[10px] uppercase tracking-widest font-black flex items-center gap-2">
                      <Car size={14} className="text-gold" aria-hidden="true" /> Transport
                    </span>
                    <span className="text-gold font-serif text-lg">{quietDignity ? 'Private' : `KSh ${calcTransport(sel).toLocaleString()}`}</span>
                  </div>
                  <p className="text-[9px] text-ink3 mt-1 uppercase tracking-widest font-medium">Billed separately per location</p>
                </div>
              )}

              {ot.total > 0 && (
                <div className="pt-6 border-t border-white/5 mb-8">
                  <div className="flex justify-between items-center">
                    <span className="text-ink3 text-[10px] uppercase tracking-widest font-black flex items-center gap-2">
                      Overtime ({ot.hours}h)
                    </span>
                    <span className="text-gold font-serif text-lg">{quietDignity ? 'Private' : `KSh ${ot.total.toLocaleString()}`}</span>
                  </div>
                </div>
              )}


              <div className="space-y-4">
                <TrustBadge icon={<ShieldCheck size={16} />} text="No payment until availability check" />
                <TrustBadge icon={<Clock size={16} />} text="2-Hour Priority Response Channel" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Suspense fallback={<div className="fixed bottom-6 right-6 w-16 h-16 pointer-events-none" />}>
        <AIAssistant />
      </Suspense>

      <AnimatePresence>
        {showLegacyModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#0A0A0A]/90 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card-bg w-full max-w-4xl rounded-3xl border border-white/10 overflow-hidden shadow-2xl relative"
            >
              <button
                onClick={() => setShowLegacyModal(false)}
                aria-label="Close preview modal"
                className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/80 rounded-full text-white transition-colors"
              >
                <X size={20} />
              </button>

              <div className="aspect-video bg-black relative flex items-center justify-center">
                <p className="text-gold uppercase tracking-[0.3em] font-bold text-xs animate-pulse flex items-center gap-2">
                  <Video size={16} /> Legacy Output Preview Simulated
                </p>
                <div className="absolute inset-0 border-[1px] border-white/10 pointer-events-none" />
              </div>

              <div className="p-8">
                <h3 className="text-2xl font-serif text-ink mb-2">The Complete Tribute</h3>
                <p className="text-sm text-ink2 max-w-2xl leading-relaxed">
                  A deeply emotional cinematic production encompassing eulogies, pre-funeral interviews, key procession moments, and graveside committals. Handcrafted to ensure the memory stays alive for generations.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {step === 1 && (
        <div className="fixed bottom-0 left-0 right-0 bg-deep/95 backdrop-blur-md border-t border-gold/20 p-4 flex items-center justify-between gap-4 z-[90] md:hidden">
          <div>
            <p className="text-[9px] uppercase tracking-widest text-ink3 font-bold mb-0.5">Your estimate</p>
            <p className="text-lg font-serif text-gold leading-none">KSh {total.toLocaleString()}</p>
            <p className="text-[9px] text-ink3 mt-0.5">No payment now</p>
          </div>
          <button
            type="button"
            onClick={() => { setStep(2); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            disabled={total === 0}
            className={cn(
              'btn-primary text-sm py-3 px-6 min-h-[48px]',
              total === 0 && 'opacity-40 cursor-not-allowed'
            )}
          >
            Continue →
          </button>
        </div>
      )}

    </div>
  );
}
